import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  Home,
  AlertTriangle,
  Calendar,
  LayoutDashboard,
  ListChecks,
  DollarSign,
  Plus,
  X,
  Pencil,
  Trash2,
  Trash,
  CheckCircle,
  Clock,
  TrendingUp,
  LogOut,
  Eye,
  Wallet,
  MessageCircle,
  CheckCheck,
  Moon,
  Sun,
  Loader2,
  HandCoins,
  FileCheck,
  Banknote,
  Check,
  Lightbulb,
  RefreshCw,
  Smartphone,
  Image,
  Paperclip,
  History,
  LineChart,
  Heart,
  ChevronLeft,
  ChevronRight,
  StickyNote,
  Undo2,
  Redo2,
  Lock,
} from "lucide-react";
import { useUndoRedo } from "./hooks/useUndoRedo";
import tipsData from "./data/tips.json";
import LoginScreen from "./components/LoginScreen";
import {
  auth,
  loans as loansApi,
  payments as paymentsApi,
  settings as settingsApi,
  receipts as receiptsApi,
  notes as notesApi,
  setSessionKickedCallback,
} from "./services/api";
import dulusBannerWhite from "./assets/images/dulus-banner-white.png";
import dulusBannerDark from "./assets/images/dulus-banner-dark.png";

const formatCurrency = (amount) =>
  `$${Number(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
  })}`;

const formatDate = (dateString) => {
  const months = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  // Extraer solo la parte de fecha si viene con hora (formato ISO completo)
  const datePart = dateString.split("T")[0];
  // Parsear la fecha como fecha local para evitar problemas de zona horaria
  const [year, month, day] = datePart.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return `${date.getDate()} ${
    months[date.getMonth()]
  } de ${date.getFullYear()}`;
};

const getPaymentDates = (
  numPeriods,
  startDate,
  paymentFrequency = "weekly",
) => {
  const dates = [];
  const start = startDate ? new Date(startDate) : new Date();

  for (let i = 0; i < numPeriods; i++) {
    const nextDate = new Date(start);
    if (paymentFrequency === "monthly") {
      // Agregar meses
      nextDate.setMonth(start.getMonth() + i);
    } else {
      // Agregar semanas
      nextDate.setDate(start.getDate() + 7 * i);
    }
    dates.push(nextDate.toISOString().split("T")[0]);
  }
  return dates;
};

const LoanCard = ({
  loan,
  onSelect,
  onEdit,
  onDelete,
  onRatingChange,
  onPayAll,
  onWhatsAppReminder,
  darkMode,
  deleted,
}) => {
  const [hover, setHover] = useState(0);
  const [swipeX, setSwipeX] = useState(0);
  const touchStartRef = useRef(0);
  const swipeXRef = useRef(0);

  const totalPaid = (loan.payments || []).reduce((sum, p) => {
    // Excluir pagos de semanas refinanciadas (inmutables)
    const wasRefinanced = loan.payments.some(
      (pay) => pay.isRefinanced && pay.originalWeek === p.week,
    );
    if (wasRefinanced) return sum;
    return sum + (p.totalPaid || 0);
  }, 0);

  // Calcular cuántas cuotas están completamente pagadas (considerando pagos directos a capital)
  const numPeriods = loan.durationWeeks;
  const capitalPerPeriod = (loan.loanPrincipal || 0) / numPeriods;

  const fullPeriodsPaid = (loan.payments || []).filter((p) => {
    // Excluir semanas que fueron refinanciadas (inmutables)
    const wasRefinanced = loan.payments.some(
      (pay) => pay.isRefinanced && pay.originalWeek === p.week,
    );
    if (wasRefinanced) return false;

    // Verificar si fue pago directo a capital (sin intereses)
    const isCapitalOnly = p.paymentHistory?.some((h) =>
      h.note?.includes("Pago directo a capital, no aplica intereses."),
    );

    // Si es pago directo a capital, verificar contra capital solamente
    const effectiveAmount = isCapitalOnly ? capitalPerPeriod : p.amount || 0;

    return (p.totalPaid || 0) >= effectiveAmount;
  }).length;

  const capitalPaid = fullPeriodsPaid * capitalPerPeriod;
  const pendingCapital = (loan.loanPrincipal || 0) - capitalPaid;

  // El préstamo está pagado cuando el capital pendiente es 0 o menos
  const isPaidOff = pendingCapital <= 0.01;
  const rating = loan.rating || 0;

  // Detectar si el préstamo tiene cuotas vencidas (solo aplica si está activo)
  const hasOverduePayments = !isPaidOff && (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (loan.payments || []).some((payment) => {
      const isCapitalOnly = payment.paymentHistory?.some((h) =>
        h.note?.includes("Pago directo a capital, no aplica intereses."),
      );
      if (isCapitalOnly) return false;
      const wasRefinanced = loan.payments.some(
        (pay) => pay.isRefinanced && pay.originalWeek === payment.week,
      );
      if (wasRefinanced) {
        if ((payment.totalPaid || 0) > 0) return false;
        const hasCompensatingPayment = loan.payments.some((pay) => {
          if (Number(pay.week) <= Number(payment.week)) return false;
          if (pay.isRefinanced) return false;
          if (loan.payments.some((p) => p.isRefinanced && p.originalWeek === pay.week)) return false;
          return (pay.totalPaid || 0) >= (pay.amount || 0);
        });
        if (hasCompensatingPayment) return false;
      }
      if (!payment.dueDate) return false;
      const [year, month, day] = payment.dueDate.split("-").map(Number);
      const dueDate = new Date(year, month - 1, day);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today && (payment.amount || 0) - (payment.totalPaid || 0) > 0;
    });
  })();

  // Próxima cuota pendiente de pago
  const nextPayment = (loan.payments || [])
    .filter((p) => {
      const wasRefinanced = loan.payments.some(
        (pay) => pay.isRefinanced && pay.originalWeek === p.week,
      );
      return !wasRefinanced && (p.totalPaid || 0) < (p.amount || 0) && p.dueDate;
    })
    .sort((a, b) => {
      const [ay, am, ad] = a.dueDate.split("-").map(Number);
      const [by, bm, bd] = b.dueDate.split("-").map(Number);
      return new Date(ay, am - 1, ad) - new Date(by, bm - 1, bd);
    })[0];
  const nextDueDate = nextPayment?.dueDate;

  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartRef.current;
    const clamped = Math.max(-150, Math.min(150, diff));
    swipeXRef.current = clamped;
    setSwipeX(clamped);
  };

  const handleTouchEnd = () => {
    const finalSwipe = swipeXRef.current;
    if (!deleted) {
      if (finalSwipe > 75) {
        // Swipe derecho - reservado para futura funcionalidad
      } else if (finalSwipe < -75) {
        // Swipe izquierdo - Recordatorio WhatsApp
        onWhatsAppReminder(loan);
      }
    }
    swipeXRef.current = 0;
    setSwipeX(0);
  };

  // Determinar el color del borde basado en el swipe
  const getBorderClass = () => {
    if (swipeX < -20) return "border-green-500 border-4"; // WhatsApp
    if (swipeX > 20) return "border-blue-500 border-4"; // Pagar todo
    return darkMode ? "border-white/30" : "border-black/20";
  };

  return (
    <div
      className={`p-4 rounded-3xl shadow-lg border hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative touch-pan-y ${
        darkMode ? "bg-gray-800" : "bg-white"
      } ${getBorderClass()}`}
      onTouchStart={deleted ? undefined : handleTouchStart}
      onTouchMove={deleted ? undefined : handleTouchMove}
      onTouchEnd={deleted ? undefined : handleTouchEnd}
      style={{
        transform: `translateX(${swipeX}px)`,
        transition: swipeX === 0 ? "transform 0.3s ease" : "none",
      }}
    >
      <div onClick={() => onSelect(loan.id)} className="cursor-pointer">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3
              className={`text-lg font-black leading-tight ${
                hasOverduePayments
                  ? "text-red-500"
                  : darkMode
                  ? "text-white"
                  : "text-gray-900"
              }`}
            >
              {loan.name}
            </h3>
            <p
              className={`text-sm mt-0.5 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {isPaidOff ? "—" : nextDueDate ? formatDate(nextDueDate) : "—"}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p
              className={`text-sm ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {isPaidOff ? "Estado" : "Pendiente"}
            </p>
            <p
              className={`text-base font-bold ${
                isPaidOff
                  ? darkMode
                    ? "text-green-400"
                    : "text-green-600"
                  : darkMode
                  ? "text-white"
                  : "text-gray-900"
              }`}
            >
              {isPaidOff ? "Pagado" : formatCurrency(pendingCapital)}
            </p>
          </div>
        </div>
        {deleted && (
          <div
            className={`text-xs font-bold px-3 py-1 rounded-full inline-block mt-2 ${
              darkMode ? "bg-red-900 text-red-300" : "bg-red-50 text-red-600"
            }`}
          >
            ELIMINADO
          </div>
        )}
      </div>
      <div
        className={`mt-3 pt-3 border-t ${
          darkMode ? "border-gray-700" : "border-gray-100"
        }`}
      >
        {!deleted && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setHover(0);
                      onRatingChange(loan.id, star === rating ? 0 : star);
                    }}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    className="transition-all duration-150 hover:scale-110"
                  >
                    <svg
                      className={`w-5 h-5 transition-all ${
                        star <= (hover || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : darkMode
                          ? "fill-gray-600 text-gray-600"
                          : "fill-gray-200 text-gray-200"
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="1"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
              {!isPaidOff && (
                <button
                  onClick={() => onEdit(loan)}
                  className={`p-2 rounded-xl transition-all ${
                    darkMode
                      ? "text-gray-300 bg-gray-700 hover:bg-gray-600"
                      : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                  }`}
                  title={"Editar Pr\u00E9stamo"}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => onDelete(loan)}
                className={`p-2 rounded-xl transition-all ${
                  darkMode
                    ? "text-red-400 bg-red-900/30 hover:bg-red-900/50"
                    : "text-red-600 bg-red-50 hover:bg-red-100"
                }`}
                title={"Eliminar Pr\u00E9stamo"}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              </div>
            </div>
          </>
        )}
        {deleted && (
          <div className="flex space-x-2">
            <button
              onClick={() => onDelete(loan)}
              className={`w-full p-2.5 rounded-xl transition-all ${
                darkMode
                  ? "text-red-400 bg-red-900/30 hover:bg-red-900/50"
                  : "text-red-600 bg-red-50 hover:bg-red-100"
              }`}
              title={"Eliminar permanentemente"}
            >
              <Trash2 className="w-4 h-4 mx-auto" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CajaView = ({ cajaAmount, onUpdateCaja, darkMode }) => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const handleEdit = () => {
    setEditValue(cajaAmount.toString());
    setIsEditing(true);
  };

  const handleSave = () => {
    if (Number(editValue) > 500000) return;
    onUpdateCaja(Number(editValue) || 0);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const description =
    "Dinero que tienes disponible para prestar. Este monto se reduce autom\u00E1ticamente al crear un nuevo pr\u00E9stamo (se resta el capital prestado). Para incrementarlo, usa el bot\u00F3n de editar y actualiza el valor manualmente. Esto te da una idea de cuanto dinero tienes disponible para prestar al momento.";

  return (
    <div className="p-4 sm:p-6">
      <h2
        className={`text-2xl font-black mb-6 ${
          darkMode ? "text-white" : "text-gray-900"
        }`}
      >
        Caja
      </h2>

      <div className="max-w-sm">
        <div
          onClick={() => setInfoModalOpen(true)}
          className={`p-6 rounded-3xl shadow-lg border hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer ${
            darkMode
              ? "bg-gray-800 border-gray-700 hover:border-gray-600"
              : "bg-white border-gray-100 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center">
            <div
              className={`p-3 rounded-2xl shadow-md ${
                darkMode
                  ? "bg-teal-900/30 text-teal-400"
                  : "bg-teal-50 text-teal-600"
              }`}
            >
              <Banknote className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  darkMode ? "text-gray-400" : "text-gray-400"
                }`}
              >
                Disponible para prestar
              </p>
              {isEditing ? (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className={`w-32 px-3 py-1.5 border-2 rounded-xl shadow-sm font-bold text-lg transition-all duration-200 ${
                      Number(editValue) > 500000
                        ? "border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        : "focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    } ${
                      darkMode
                        ? `bg-gray-700 ${
                            Number(editValue) > 500000
                              ? "border-red-500"
                              : "border-gray-600"
                          } text-white`
                        : `bg-white ${
                            Number(editValue) > 500000
                              ? "border-red-500"
                              : "border-gray-200"
                          } text-gray-900`
                    }`}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                    className="p-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-all duration-200"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                    className={`p-1.5 rounded-lg transition-all duration-200 ${
                      darkMode
                        ? "text-gray-300 bg-gray-700 hover:bg-gray-600"
                        : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <p
                    className={`text-2xl font-black ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {formatCurrency(cajaAmount)}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                    className={`p-1.5 rounded-lg transition-all duration-200 ${
                      darkMode
                        ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {infoModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setInfoModalOpen(false)}
        >
          <div
            className={`rounded-3xl shadow-2xl max-w-md w-full p-8 relative ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setInfoModalOpen(false)}
              className={`absolute top-4 right-4 p-2 rounded-xl transition-all duration-200 ${
                darkMode
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                  : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              }`}
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center mb-6">
              <div
                className={`p-3 rounded-2xl shadow-md ${
                  darkMode
                    ? "bg-teal-900/30 text-teal-400"
                    : "bg-teal-50 text-teal-600"
                }`}
              >
                <Banknote className="w-6 h-6" />
              </div>
              <h3
                className={`text-2xl font-black ml-4 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Caja chica
              </h3>
            </div>
            <p
              className={`text-base leading-relaxed ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const categoryConfig = {
  finanzas: { label: "Finanzas", icon: TrendingUp, color: "indigo" },
  prestamos: { label: "Pr\u00E9stamos", icon: HandCoins, color: "emerald" },
  citas: { label: "Cita C\u00E9lebre", icon: Lightbulb, color: "amber" },
  app: { label: "Uso de la App", icon: Smartphone, color: "violet" },
};

const categoryColors = {
  indigo: {
    badge: "bg-indigo-100 text-indigo-700",
    badgeDark: "bg-indigo-900/40 text-indigo-300",
    icon: "bg-indigo-50 text-indigo-600",
    iconDark: "bg-indigo-900/30 text-indigo-400",
    accent: "from-indigo-500 to-indigo-600",
    hex: "#6366f1",
  },
  emerald: {
    badge: "bg-emerald-100 text-emerald-700",
    badgeDark: "bg-emerald-900/40 text-emerald-300",
    icon: "bg-emerald-50 text-emerald-600",
    iconDark: "bg-emerald-900/30 text-emerald-400",
    accent: "from-emerald-500 to-emerald-600",
    hex: "#10b981",
  },
  amber: {
    badge: "bg-amber-100 text-amber-700",
    badgeDark: "bg-amber-900/40 text-amber-300",
    icon: "bg-amber-50 text-amber-600",
    iconDark: "bg-amber-900/30 text-amber-400",
    accent: "from-amber-500 to-amber-600",
    hex: "#f59e0b",
  },
  violet: {
    badge: "bg-violet-100 text-violet-700",
    badgeDark: "bg-violet-900/40 text-violet-300",
    icon: "bg-violet-50 text-violet-600",
    iconDark: "bg-violet-900/30 text-violet-400",
    accent: "from-violet-500 to-violet-600",
    hex: "#8b5cf6",
  },
};

const getTipOfTheDay = () => {
  const today = new Date();
  const seed =
    today.getFullYear() * 10000 +
    (today.getMonth() + 1) * 100 +
    today.getDate();
  return tipsData[seed % tipsData.length];
};

const TipsView = ({ darkMode, favoriteTips, onToggleFavorite }) => {
  const [currentTip, setCurrentTip] = useState(() => getTipOfTheDay());
  const [isShuffling, setIsShuffling] = useState(false);
  const [fadeState, setFadeState] = useState("visible");
  const [favoritesMode, setFavoritesMode] = useState(false);
  const [favIndex, setFavIndex] = useState(0);
  const seenRef = useRef(new Set());
  const shuffleTimerRef = useRef(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("tips-seen");
    if (stored) {
      try {
        seenRef.current = new Set(JSON.parse(stored));
      } catch (e) {
        /* ignore */
      }
    }
    seenRef.current.add(currentTip.id);
    sessionStorage.setItem("tips-seen", JSON.stringify([...seenRef.current]));
    return () => {
      if (shuffleTimerRef.current) clearTimeout(shuffleTimerRef.current);
    };
  }, []);

  // Sync currentTip when favoriteTips changes while in favorites mode
  useEffect(() => {
    if (!favoritesMode) return;
    if (favoriteTips.length === 0) {
      setFavoritesMode(false);
      return;
    }
    const clamped = Math.min(favIndex, favoriteTips.length - 1);
    if (clamped !== favIndex) setFavIndex(clamped);
    const tip = tipsData.find((t) => t.id === favoriteTips[clamped]);
    if (tip) setCurrentTip(tip);
  }, [favoriteTips]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRandomUnseen = () => {
    const unseen = tipsData.filter((t) => !seenRef.current.has(t.id));
    const pool = unseen.length > 0 ? unseen : tipsData;
    if (unseen.length === 0) seenRef.current.clear();
    const pick = pool[Math.floor(Math.random() * pool.length)];
    seenRef.current.add(pick.id);
    sessionStorage.setItem("tips-seen", JSON.stringify([...seenRef.current]));
    return pick;
  };

  const handleShuffle = () => {
    if (isShuffling) return;
    setIsShuffling(true);
    setFadeState("fading-out");
    shuffleTimerRef.current = setTimeout(() => {
      const newTip = getRandomUnseen();
      setCurrentTip(newTip);
      setFadeState("fading-in");
      shuffleTimerRef.current = setTimeout(() => {
        setFadeState("visible");
        setIsShuffling(false);
      }, 600);
    }, 3000);
  };

  const handleFavNav = (direction) => {
    if (isShuffling || favoriteTips.length === 0) return;
    const newIdx =
      direction === "next"
        ? (favIndex + 1) % favoriteTips.length
        : (favIndex - 1 + favoriteTips.length) % favoriteTips.length;
    const tip = tipsData.find((t) => t.id === favoriteTips[newIdx]);
    if (!tip) return;
    setIsShuffling(true);
    setFadeState("fading-out");
    shuffleTimerRef.current = setTimeout(() => {
      setFavIndex(newIdx);
      setCurrentTip(tip);
      setFadeState("fading-in");
      shuffleTimerRef.current = setTimeout(() => {
        setFadeState("visible");
        setIsShuffling(false);
      }, 400);
    }, 200);
  };

  const handleToggleFavoritesMode = () => {
    if (favoritesMode) {
      setFavoritesMode(false);
    } else {
      if (favoriteTips.length === 0) return;
      const idx = favoriteTips.indexOf(currentTip.id);
      const startIdx = idx >= 0 ? idx : 0;
      setFavIndex(startIdx);
      const tip = tipsData.find((t) => t.id === favoriteTips[startIdx]);
      if (tip) setCurrentTip(tip);
      setFavoritesMode(true);
    }
  };

  const isFavorite = favoriteTips.includes(currentTip.id);
  const hasFavorites = favoriteTips.length > 0;
  const cat = categoryConfig[currentTip.category] || categoryConfig.finanzas;
  const colors = categoryColors[cat.color];
  const CatIcon = cat.icon;

  return (
    <div
      className="p-4 sm:p-6 flex flex-col items-center"
      style={{ minHeight: "calc(100vh - 10rem)" }}
    >
      {/* Header row: title + favorites mode toggle */}
      <div className="flex items-center gap-3 mb-4 self-start">
        <h2
          className={`text-2xl font-black ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Tips
        </h2>
        <button
          onClick={handleToggleFavoritesMode}
          disabled={!hasFavorites}
          className={`p-1.5 rounded-xl transition-all duration-200 ${
            !hasFavorites
              ? "opacity-30 cursor-not-allowed"
              : favoritesMode
              ? "text-red-500"
              : darkMode
              ? "text-gray-400 hover:text-red-400"
              : "text-gray-400 hover:text-red-500"
          }`}
          title={favoritesMode ? "Ver todos los tips" : "Ver tips favoritos"}
        >
          <Heart
            className={`w-5 h-5 ${favoritesMode ? "fill-red-500" : ""}`}
          />
        </button>
      </div>

      <div
        className={`w-full max-w-lg rounded-2xl shadow-lg border overflow-hidden flex flex-col flex-1 ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`}
      >
        {/* Accent bar */}
        <div className={`h-1.5 bg-gradient-to-r ${colors.accent}`} />

        <div className="p-6 sm:p-8 flex flex-col flex-1">
          {/* Category badge + heart + navigation buttons */}
          <div className="flex items-center justify-between mb-6">
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                darkMode ? colors.badgeDark : colors.badge
              }`}
            >
              <CatIcon className="w-3.5 h-3.5" />
              {cat.label}
            </div>
            <div className="flex items-center gap-1">
              {/* Heart: favorite/unfavorite current tip */}
              <button
                onClick={() => onToggleFavorite(currentTip.id)}
                className={`p-2.5 rounded-xl transition-all duration-200 ${
                  isFavorite
                    ? "text-red-500"
                    : darkMode
                    ? "text-gray-500 hover:text-red-400"
                    : "text-gray-400 hover:text-red-500"
                }`}
                title={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
              >
                <Heart
                  className={`w-5 h-5 ${isFavorite ? "fill-red-500" : ""}`}
                />
              </button>
              {/* Reload vs prev/next */}
              {favoritesMode ? (
                <>
                  <button
                    onClick={() => handleFavNav("prev")}
                    disabled={isShuffling}
                    className={`p-2.5 rounded-xl transition-all duration-300 ${
                      isShuffling
                        ? darkMode
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : darkMode
                        ? "text-gray-300 hover:bg-gray-700 active:scale-90"
                        : "text-gray-500 hover:bg-gray-100 active:scale-90"
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleFavNav("next")}
                    disabled={isShuffling}
                    className={`p-2.5 rounded-xl transition-all duration-300 ${
                      isShuffling
                        ? darkMode
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : darkMode
                        ? "text-gray-300 hover:bg-gray-700 active:scale-90"
                        : "text-gray-500 hover:bg-gray-100 active:scale-90"
                    }`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <button
                  onClick={handleShuffle}
                  disabled={isShuffling}
                  className={`p-2.5 rounded-xl transition-all duration-300 ${
                    isShuffling
                      ? darkMode
                        ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : darkMode
                      ? "text-gray-300 hover:bg-gray-700 active:scale-90"
                      : "text-gray-500 hover:bg-gray-100 active:scale-90"
                  }`}
                >
                  <RefreshCw
                    className={`w-5 h-5 ${isShuffling ? "animate-spin" : ""}`}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Tip content */}
          <div className="flex-1 flex flex-col items-center justify-center relative min-h-[250px]">
            {/* Shuffling spinner (only for random, not favorites nav) */}
            {isShuffling && fadeState !== "fading-in" && !favoritesMode && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div
                  className={`p-4 rounded-2xl ${
                    darkMode ? colors.iconDark : colors.icon
                  }`}
                >
                  <Lightbulb className="w-8 h-8 animate-pulse" />
                </div>
                <div className="flex gap-1.5">
                  <div
                    className={`w-2 h-2 rounded-full animate-bounce ${
                      darkMode ? "bg-gray-500" : "bg-gray-300"
                    }`}
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className={`w-2 h-2 rounded-full animate-bounce ${
                      darkMode ? "bg-gray-500" : "bg-gray-300"
                    }`}
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className={`w-2 h-2 rounded-full animate-bounce ${
                      darkMode ? "bg-gray-500" : "bg-gray-300"
                    }`}
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            )}

            {/* Tip text + author */}
            <div
              className={`text-center transition-all duration-500 ${
                fadeState === "fading-out"
                  ? "opacity-0 scale-95"
                  : fadeState === "fading-in"
                  ? "opacity-0 scale-105"
                  : "opacity-100 scale-100"
              }`}
              style={
                fadeState === "fading-in"
                  ? { animation: "tipFadeIn 0.6s ease-out forwards" }
                  : {}
              }
            >
              <p
                className={`text-lg sm:text-xl leading-relaxed font-medium ${
                  darkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {currentTip.author
                  ? `\u201C${currentTip.text}\u201D`
                  : currentTip.text}
              </p>
              {currentTip.author && (
                <p
                  className={`mt-4 text-sm font-semibold italic ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {`\u2014 ${currentTip.author}`}
                </p>
              )}
              {favoritesMode && favoriteTips.length > 1 && (
                <p
                  className={`mt-4 text-xs ${
                    darkMode ? "text-gray-600" : "text-gray-300"
                  }`}
                >
                  {favIndex + 1} / {favoriteTips.length}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tipFadeIn {
          from { opacity: 0; transform: scale(1.05); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

const Dashboard = ({ loans, darkMode, cajaAmount, hiddenBoards = [], onNavigateToFilter }) => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);

  let totalPrincipal = 0,
    totalPaid = 0,
    totalRevenue = 0,
    totalPendingBalance = 0;
  let totalCapitalPaid = 0,
    activeLoansCount = 0,
    overdueLoanCount = 0,
    totalPendingCapital = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalizar a inicio del día

  loans.forEach((loan) => {
    totalPrincipal += loan.loanPrincipal || 0;

    const paidAmount = (loan.payments || []).reduce((sum, p) => {
      // Excluir pagos de semanas refinanciadas (inmutables)
      const wasRefinanced = loan.payments.some(
        (pay) => pay.isRefinanced && pay.originalWeek === p.week,
      );
      if (wasRefinanced) return sum;
      return sum + (p.totalPaid || 0);
    }, 0);
    totalPaid += paidAmount;

    // Calcular ganancia real (solo intereses cobrados)
    const numWeeks = loan.durationWeeks;
    const capitalPerWeek = (loan.loanPrincipal || 0) / numWeeks;
    const interestPerWeek = Math.max(
      0,
      (loan.paymentAmount || 0) - capitalPerWeek,
    );

    // Calcular cuántas cuotas están completamente pagadas
    const fullPeriodsPaid = (loan.payments || []).filter((p) => {
      const wasRefinanced = loan.payments.some(
        (pay) => pay.isRefinanced && pay.originalWeek === p.week,
      );
      if (wasRefinanced) return false;

      const isCapitalOnly = p.paymentHistory?.some((h) =>
        h.note?.includes("Pago directo a capital, no aplica intereses."),
      );

      const effectiveAmount = isCapitalOnly ? capitalPerWeek : p.amount || 0;
      return (p.totalPaid || 0) >= effectiveAmount;
    }).length;

    const capitalPaid = fullPeriodsPaid * capitalPerWeek;
    const pendingCapital = (loan.loanPrincipal || 0) - capitalPaid;

    // Acumular capital pagado total
    totalCapitalPaid += capitalPaid;

    // Contar préstamos activos (con capital pendiente)
    const isActive = pendingCapital > 0.01;
    if (isActive) {
      activeLoansCount++;
      totalPendingCapital += pendingCapital;
    }

    // Verificar si este préstamo tiene cuotas vencidas (solo para préstamos activos)
    let hasOverduePayments = false;

    // Por cada semana, sumar el rédito colectado y verificar vencidos
    (loan.payments || []).forEach((payment) => {
      const totalPaidThisWeek = payment.totalPaid || 0;

      const isCapitalOnly = payment.paymentHistory?.some((h) =>
        h.note?.includes("Pago directo a capital, no aplica intereses."),
      );

      if (isCapitalOnly) {
        return;
      }

      // Para pagos normales, calcular el interés cobrado
      if (totalPaidThisWeek >= (payment.amount || 0)) {
        totalRevenue += interestPerWeek;
      } else if (totalPaidThisWeek >= capitalPerWeek) {
        totalRevenue += Math.min(
          totalPaidThisWeek - capitalPerWeek,
          interestPerWeek,
        );
      } else if (totalPaidThisWeek > 0) {
        const isPaidInterestOnly = payment.paymentHistory?.some(
          (h) => h.amount <= interestPerWeek && h.amount === totalPaidThisWeek,
        );
        if (isPaidInterestOnly) {
          totalRevenue += totalPaidThisWeek;
        }
      }

      // Verificar si este pago está vencido (solo si el préstamo está activo)
      if (isActive) {
        const wasRefinanced = loan.payments.some(
          (pay) => pay.isRefinanced && pay.originalWeek === payment.week,
        );
        let skipDueToRefinance = false;
        if (wasRefinanced) {
          if ((payment.totalPaid || 0) > 0) {
            skipDueToRefinance = true;
          } else {
            const hasCompensatingPayment = (loan.payments || []).some((pay) => {
              if (Number(pay.week) <= Number(payment.week)) return false;
              if (pay.isRefinanced) return false;
              if ((loan.payments || []).some((p) => p.isRefinanced && p.originalWeek === pay.week)) return false;
              return (pay.totalPaid || 0) >= (pay.amount || 0);
            });
            skipDueToRefinance = hasCompensatingPayment;
          }
        }
        if (!skipDueToRefinance && payment.dueDate) {
          const [year, month, day] = payment.dueDate.split("-").map(Number);
          const dueDate = new Date(year, month - 1, day);
          dueDate.setHours(0, 0, 0, 0);

          if (dueDate < today) {
            const remaining = (payment.amount || 0) - (payment.totalPaid || 0);
            if (remaining > 0) {
              hasOverduePayments = true;
            }
          }
        }
      }
    });

    // Si el préstamo activo tiene pagos vencidos, incrementar el contador
    if (hasOverduePayments) {
      overdueLoanCount++;
    }

    // Calcular el monto efectivamente pendiente de este préstamo
    if (pendingCapital > 0) {
      // Hay capital pendiente, calcular cuánto falta cobrar
      const effectiveTotalRepayable = (loan.payments || []).reduce((sum, p) => {
        const wasRefinanced = loan.payments.some(
          (pay) => pay.isRefinanced && pay.originalWeek === p.week,
        );
        if (wasRefinanced) return sum;

        const isCapitalOnly = p.paymentHistory?.some((h) =>
          h.note?.includes("Pago directo a capital, no aplica intereses."),
        );

        // Si fue pago directo a capital, solo cuenta el capital
        const effectiveAmount = isCapitalOnly ? capitalPerWeek : p.amount || 0;
        return sum + effectiveAmount;
      }, 0);

      const loanPendingBalance = effectiveTotalRepayable - paidAmount;
      totalPendingBalance += Math.max(0, loanPendingBalance);
    }
    // Si pendingCapital <= 0, no hay nada pendiente para este préstamo
  });

  const totalCollected = totalCapitalPaid + totalRevenue;

  const metrics = {
    totalPrincipal,
    totalRevenue,
    pendingBalance: totalPendingBalance,
    totalCollected,
    activeLoansCount,
    overdueLoanCount,
    totalPendingCapital,
  };

  const metricDescriptions = {
    Histórico:
      "Todo el dinero que has prestado a lo largo del tiempo. Este valor es acumulativo y solo aumenta cuando creas nuevos préstamos, nunca disminuye aunque los préstamos se paguen.",
    Prestado:
      "Total de capital (sin intereses) pendiente por cobrar en préstamos activos. Representa únicamente el capital que aún no ha sido recuperado, sin contar los intereses.",
    Proyectado:
      "Lo que está proyectado a cobrar (Capital + Intereses aún no cobrados). Este valor cambia según se creen nuevos préstamos o se hagan pagos. Puede llegar a cero cuando todos los préstamos están saldados.",
    Cobrado:
      "Total de dinero que ha entrado al negocio (capital recuperado + intereses cobrados). Representa el cash flow real acumulado que has recibido de todos tus préstamos.",
    Ganancia:
      "Total de intereses que has ganado de todos los préstamos. Este valor es acumulativo y representa tus ganancias históricas realizadas, solo aumenta cuando se cobran intereses.",
    Activos:
      "Número de préstamos que actualmente tienen saldo pendiente. Un préstamo se considera activo cuando aún no se ha pagado todo el capital prestado.",
    Atrasados:
      "Número de préstamos activos que tienen una o más cuotas vencidas. Estos préstamos requieren seguimiento inmediato para gestionar la cobranza y evitar mayor atraso.",
    Caja:
      "Dinero que tienes disponible para prestar. Este monto se reduce automáticamente al crear un nuevo préstamo. Para incrementarlo, actualízalo manualmente desde la vista de Caja.",
  };

  const cardData = [
    {
      title: "Histórico",
      value: metrics.totalPrincipal,
      iconBg: "bg-yellow-50",
      iconColor: "text-yellow-600",
      icon: <History className="w-6 h-6" />,
      darkIconBg: "bg-yellow-900/30",
      darkIconColor: "text-yellow-400",
    },
    {
      title: "Prestado",
      value: metrics.totalPendingCapital,
      iconBg: "bg-red-50",
      iconColor: "text-red-600",
      icon: <HandCoins className="w-6 h-6" />,
      darkIconBg: "bg-red-900/30",
      darkIconColor: "text-red-400",
    },
    {
      title: "Proyectado",
      value: metrics.pendingBalance,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-600",
      icon: <LineChart className="w-6 h-6" />,
      darkIconBg: "bg-indigo-900/30",
      darkIconColor: "text-indigo-400",
    },
    {
      title: "Cobrado",
      value: metrics.totalCollected,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      icon: <Wallet className="w-6 h-6" />,
      darkIconBg: "bg-blue-900/30",
      darkIconColor: "text-blue-400",
    },
    {
      title: "Ganancia",
      value: metrics.totalRevenue,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
      icon: <TrendingUp className="w-6 h-6" />,
      darkIconBg: "bg-green-900/30",
      darkIconColor: "text-green-400",
    },
    {
      title: "Activos",
      value: metrics.activeLoansCount,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
      icon: <FileCheck className="w-6 h-6" />,
      darkIconBg: "bg-purple-900/30",
      darkIconColor: "text-purple-400",
      isCount: true,
    },
    {
      title: "Atrasados",
      value: metrics.overdueLoanCount,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
      icon: <AlertTriangle className="w-6 h-6" />,
      darkIconBg: "bg-orange-900/30",
      darkIconColor: "text-orange-400",
      isCount: true,
    },
    {
      title: "Caja",
      value: cajaAmount,
      iconBg: "bg-teal-50",
      iconColor: "text-teal-600",
      icon: <Banknote className="w-6 h-6" />,
      darkIconBg: "bg-teal-900/30",
      darkIconColor: "text-teal-400",
    },
  ];

  const handleCardClick = (metric) => {
    setSelectedMetric(metric);
    setInfoModalOpen(true);
  };

  return (
    <div className="p-4 sm:p-6">
      <h2
        className={`text-3xl font-black mb-6 ${
          darkMode ? "text-white" : "text-gray-900"
        }`}
      >
        Resumen
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
        {cardData.filter(item => !hiddenBoards.includes(item.title)).map((item, idx) => (
          <div
            key={idx}
            onClick={() => handleCardClick(item)}
            className={`p-6 rounded-3xl shadow-lg border cursor-pointer ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <div className="flex items-center">
              <div
                className={`p-3 rounded-2xl shadow-md ${
                  darkMode ? item.darkIconBg : item.iconBg
                } ${darkMode ? item.darkIconColor : item.iconColor}`}
              >
                {item.icon}
              </div>
              <div className="ml-4">
                <p
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    darkMode ? "text-gray-400" : "text-gray-400"
                  }`}
                >
                  {item.title}
                </p>
                <p
                  className={`text-2xl font-black ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {item.title === "Atrasados" && onNavigateToFilter ? (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToFilter("loans", "status", "overdue");
                      }}
                      className="cursor-pointer"
                    >
                      {item.value}
                    </span>
                  ) : (
                    item.isCount ? item.value : formatCurrency(item.value)
                  )}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de información */}
      {infoModalOpen && selectedMetric && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setInfoModalOpen(false)}
        >
          <div
            className={`rounded-3xl shadow-2xl max-w-md w-full p-8 relative ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setInfoModalOpen(false)}
              className={`absolute top-4 right-4 p-2 rounded-xl transition-all duration-200 ${
                darkMode
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                  : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              }`}
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex items-center mb-6">
              <div
                className={`p-3 rounded-2xl ${selectedMetric.iconBg} ${selectedMetric.iconColor} shadow-md`}
              >
                {selectedMetric.icon}
              </div>
              <h3
                className={`text-2xl font-black ml-4 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {selectedMetric.title}
              </h3>
            </div>

            <p
              className={`text-base leading-relaxed ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {metricDescriptions[selectedMetric.title]}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const LoanDetailsView = ({
  loan,
  onRegisterPayment,
  onRefinanceBalance,
  onCapitalPayment,
  onRevertPayment,
  darkMode,
  actionLoading,
  readOnly,
  onBack,
}) => {
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentType, setPaymentType] = useState("full");
  const [paymentNote, setPaymentNote] = useState("");
  const [capitalAmount, setCapitalAmount] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [activeBadge, setActiveBadge] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const receiptInputRef = useRef(null);

  useEffect(() => {
    if (activeBadge) {
      const timer = setTimeout(() => setActiveBadge(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [activeBadge]);

  const payments = loan.payments || [];
  const totalRepayable = loan.totalRepayable || 0;
  const loanPrincipal = loan.loanPrincipal || totalRepayable;
  const paymentAmount_expected = loan.paymentAmount || 0;
  const paymentFrequency = loan.paymentFrequency || "weekly";
  const periodLabel = "Cuota";
  const periodLabelPlural = "Cuotas";

  // Calcular el total pagado, EXCLUYENDO pagos de semanas refinanciadas (inmutables)
  const totalPaid = payments.reduce((sum, p) => {
    // Verificar si esta semana fue refinanciada (su saldo se movió a otra semana)
    const wasRefinanced = payments.some(
      (pay) => pay.isRefinanced && pay.originalWeek === p.week,
    );

    // Si fue refinanciada, no sumar sus pagos porque esa semana ya no es cobrable
    if (wasRefinanced) return sum;

    return sum + (p.totalPaid || 0);
  }, 0);

  // La cuota revertible es la que tiene el entry positivo más reciente globalmente
  // y no tiene una reversión posterior. Solo esa cuota muestra el botón "Revertir".
  const globallyRevertiblePaymentId = (() => {
    let bestId = null;
    let bestDate = null;
    let bestUpdatedAt = null;
    for (const p of payments) {
      const hist = p.paymentHistory || [];
      const lastPosIdx = hist.reduce((acc, h, i) => (h.amount > 0 ? i : acc), -1);
      if (lastPosIdx === -1) continue;
      if (hist.slice(lastPosIdx + 1).some((h) => h.amount < 0)) continue;
      const date = hist[lastPosIdx].date || "";
      const updatedAt = p.updatedAt || "";
      if (!bestDate || date > bestDate || (date === bestDate && updatedAt > bestUpdatedAt)) {
        bestDate = date;
        bestUpdatedAt = updatedAt;
        bestId = p.id;
      }
    }
    return bestId;
  })();

  // Usar SIEMPRE la duración original del préstamo, no la cantidad de pagos actuales
  const numPeriods = loan.durationWeeks;
  const capitalPerPeriod = loanPrincipal / numPeriods;
  const interestPerPeriod = paymentAmount_expected - capitalPerPeriod;

  // Calcular el total a cobrar excluyendo semanas refinanciadas (inmutables)
  // Para cuotas pagadas con abono a capital, usar capitalPerPeriod (sin intereses)
  const effectiveTotalRepayable = payments.reduce((sum, p) => {
    const wasRefinanced = payments.some(
      (pay) => pay.isRefinanced && pay.originalWeek === p.week,
    );
    if (wasRefinanced) return sum;

    const isCapitalOnly = p.paymentHistory?.some((h) =>
      h.note?.includes("Pago directo a capital, no aplica intereses."),
    );

    return sum + (isCapitalOnly ? capitalPerPeriod : p.amount || 0);
  }, 0);

  const pendingBalance = effectiveTotalRepayable - totalPaid;

  // Calcular cuotas pagadas correctamente
  const fullPeriodsPaid = payments.filter((p) => {
    // Excluir semanas que fueron refinanciadas (inmutables)
    const wasRefinanced = payments.some(
      (pay) => pay.isRefinanced && pay.originalWeek === p.week,
    );
    if (wasRefinanced) return false;

    // Verificar si fue pago directo a capital (sin intereses)
    const isCapitalOnly = p.paymentHistory?.some((h) =>
      h.note?.includes("Pago directo a capital, no aplica intereses."),
    );

    // Si es pago directo a capital, verificar contra capital solamente
    const effectiveAmount = isCapitalOnly ? capitalPerPeriod : p.amount || 0;

    return (p.totalPaid || 0) >= effectiveAmount;
  }).length;

  // Total de cuotas = todas las semanas (incluyendo refinanciadas)
  const totalCollectablePayments = payments.length;

  const capitalPaid = fullPeriodsPaid * capitalPerPeriod;
  const pendingCapital = loanPrincipal - capitalPaid;

  // Si el capital pendiente es 0, el total pendiente también debe ser 0
  // (ignorar intereses no cobrados de semanas pagadas solo con capital)
  const adjustedPendingBalance = pendingCapital <= 0 ? 0 : pendingBalance;

  const balanceColor =
    adjustedPendingBalance > 0 ? "text-red-500" : "text-emerald-500";

  const handleOpenPaymentModal = (payment) => {
    setSelectedPayment(payment);
    setPaymentType("full");
    setPaymentNote("");
    setCapitalAmount("");
    setPaymentModalOpen(true);
  };

  const handleOpenDetailsModal = (payment) => {
    setSelectedPayment(payment);
    setDetailsModalOpen(true);
  };

  const handleRegisterPayment = () => {
    const remainingAmount =
      (selectedPayment.amount || 0) - (selectedPayment.totalPaid || 0);
    const interestAlreadyPaid = Math.min(
      selectedPayment.totalPaid || 0,
      interestPerPeriod,
    );
    const remainingInterest = Math.max(
      0,
      interestPerPeriod - interestAlreadyPaid,
    );

    if (paymentType === "capital") {
      // Abono a capital - seleccionar hasta qué semana pagar
      const untilWeek = Number(capitalAmount);
      if (!untilWeek || untilWeek <= selectedPayment.week) {
        showMsg(
          "Por favor selecciona una semana posterior a la actual para el abono a capital",
          true,
        );
        return;
      }

      // Calcular el total a pagar
      const weeksToPay = untilWeek - selectedPayment.week + 1;
      const currentWeekAmount = selectedPayment.amount; // Pago completo de la semana actual
      const additionalWeeks = weeksToPay - 1;
      const additionalAmount = additionalWeeks * capitalPerPeriod; // Solo capital para semanas adicionales
      const totalAmount = currentWeekAmount + additionalAmount;

      // Guardar datos para el modal de confirmación
      setConfirmData({
        untilWeek,
        currentWeekAmount,
        additionalWeeks,
        additionalAmount,
        totalAmount,
        currentWeek: selectedPayment.week,
      });
      setConfirmModalOpen(true);
      return;
    }

    let amount;
    if (paymentType === "full") {
      // Pagar lo que resta (puede ser solo capital si ya se pagó el rédito)
      amount = remainingAmount;
    } else {
      // Solo rédito (solo si todavía hay rédito por pagar)
      amount = remainingInterest;
    }

    onRegisterPayment(
      loan.id,
      selectedPayment.id,
      amount,
      paymentNote,
      paymentType,
      receiptFile,
    );
    setPaymentModalOpen(false);
    setPaymentType("full");
    setPaymentNote("");
    setCapitalAmount("");
    setSelectedPayment(null);
    clearReceipt();
  };

  const handleConfirmCapital = () => {
    if (!confirmData) return;

    // Llamar a la función especial para abono a capital
    onCapitalPayment(
      loan.id,
      selectedPayment.id,
      confirmData.untilWeek,
      paymentNote,
    );

    // Cerrar ambos modales y limpiar estados
    setConfirmModalOpen(false);
    setPaymentModalOpen(false);
    setPaymentType("full");
    setPaymentNote("");
    setCapitalAmount("");
    setSelectedPayment(null);
    setConfirmData(null);
  };

  const handleReceiptSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/heic",
      "image/heif",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Formato no permitido. Usa PNG, JPG, WebP o HEIC.");
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      alert("La imagen debe pesar menos de 1 MB.");
      return;
    }
    setReceiptFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    if (receiptInputRef.current) receiptInputRef.current.value = "";
  };

  const handleViewReceipt = async (receiptKey) => {
    try {
      const { downloadUrl } = await receiptsApi.getDownloadUrl({ receiptKey });
      setReceiptImageUrl(downloadUrl);
    } catch (err) {
      alert("Error al cargar comprobante");
    }
  };

  const handleRefinance = (paymentId) => {
    onRefinanceBalance(loan.id, paymentId);
    setDetailsModalOpen(false);
  };

  const handleRevert = (payment) => {
    const lastPositive = [...(payment.paymentHistory || [])].reverse().find((h) => h.amount > 0);
    if (!lastPositive) return;
    if (
      !window.confirm(
        `¿Revertir el pago de ${formatCurrency(lastPositive.amount)} del ${formatDate(lastPositive.date)}?\n\nEsta acción no se puede deshacer.`,
      )
    )
      return;
    onRevertPayment(loan.id, payment.id);
    setDetailsModalOpen(false);
  };

  const isOverdue = (payment) => {
    const wasRefinanced = payments.some(
      (pay) => pay.isRefinanced && pay.originalWeek === payment.week,
    );
    if (wasRefinanced) {
      if ((payment.totalPaid || 0) > 0) return false;
      const hasCompensatingPayment = payments.some((pay) => {
        if (Number(pay.week) <= Number(payment.week)) return false;
        if (pay.isRefinanced) return false;
        if (payments.some((p) => p.isRefinanced && p.originalWeek === pay.week)) return false;
        return (pay.totalPaid || 0) >= (pay.amount || 0);
      });
      if (hasCompensatingPayment) return false;
    }

    // Verificar si este pago fue directo a capital (sin intereses)
    const isCapitalOnly = payment.paymentHistory?.some((h) =>
      h.note?.includes("Pago directo a capital, no aplica intereses."),
    );

    // Si es pago directo a capital, verificar contra capital, si no, contra el monto total
    const effectiveAmount = isCapitalOnly ? capitalPerPeriod : payment.amount;

    if (payment.totalPaid >= effectiveAmount) return false;

    // Obtener fecha actual sin hora (medianoche local)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Parsear la fecha de vencimiento como fecha local sin hora
    const [year, month, day] = payment.dueDate.split("-").map(Number);
    const dueDate = new Date(year, month - 1, day);
    dueDate.setHours(0, 0, 0, 0);

    return today > dueDate;
  };

  const hasOverduePayments =
    pendingCapital > 0.01 && payments.some((p) => isOverdue(p));

  const isRefinanced = (payment) => {
    return payments.some(
      (p) => p.isRefinanced && p.originalWeek === payment.week,
    );
  };

  const getPaymentBadges = (payment) => {
    const badges = [];
    const expected = payment.amount || 0;
    const paid = payment.totalPaid || 0;
    const isCapitalOnly = payment.paymentHistory?.some((h) =>
      h.note?.includes("Pago directo a capital, no aplica intereses."),
    );
    const effectiveExpected = isCapitalOnly ? capitalPerPeriod : expected;
    const remaining = effectiveExpected - paid;
    const refinanced = isRefinanced(payment);
    const overdue = isOverdue(payment);

    if (refinanced) {
      badges.push({ color: "bg-violet-500", label: "Cuota refinanciada." });
      return badges;
    }

    if (remaining <= 0) {
      badges.push({
        color: "bg-green-500",
        label: "Cuota pagada, capital + rédito.",
      });
      return badges;
    }

    if (paid > 0) {
      badges.push({ color: "bg-yellow-400", label: "Cuota con pago parcial." });
    }

    if (overdue) {
      badges.push({ color: "bg-red-500", label: "Cuota vencida." });
    }

    return badges;
  };

  if (!loan.id) {
    return (
      <div className="p-10 text-center text-gray-500">
        Error: Detalles no cargados
      </div>
    );
  }

  return (
    <div className="mt-8 p-4 sm:p-6">
      <div
        className={`relative shadow-xl rounded-3xl p-6 mb-8 border ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`}
      >
        {onBack && (
          <button
            onClick={onBack}
            className={`absolute top-7 left-6 p-1.5 rounded-full transition-colors ${
              darkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div className="mb-4 text-center">
          <h2
            className={`text-3xl font-black ${
              hasOverduePayments
                ? "text-red-500"
                : darkMode
                ? "text-white"
                : "text-gray-800"
            }`}
          >
            {loan.name}
          </h2>
          <p
            className={`text-md mt-1 font-medium ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {loan.description}
          </p>

          <hr
            className={`my-4 ${
              darkMode ? "border-gray-700" : "border-gray-100"
            }`}
          />

          {/* Info del préstamo */}
          <div
            className={`mt-4 space-y-2 text-xs ${
              darkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-2">
                <span className="font-semibold uppercase tracking-wider">
                  Creado
                </span>{" "}
                <span
                  className={`font-medium ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {loan.createdAt ? formatDate(loan.createdAt) : "—"}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <span className="font-semibold uppercase tracking-wider">
                  Frecuencia
                </span>{" "}
                <span
                  className={`font-medium ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {paymentFrequency === "monthly" ? "mensual" : "semanal"}
                </span>
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-2">
                <span className="font-semibold uppercase tracking-wider">
                  Préstamo
                </span>{" "}
                <span className="font-medium text-green-600">
                  {formatCurrency(loan.loanPrincipal)}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <span className="font-semibold uppercase tracking-wider">
                  Cuotas
                </span>{" "}
                <span className="font-medium">
                  <span className="text-green-600">{fullPeriodsPaid}</span>
                  <span
                    className={darkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    /
                  </span>
                  <span className="text-red-600">
                    {totalCollectablePayments}
                  </span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <hr
          className={`my-4 ${darkMode ? "border-gray-700" : "border-gray-100"}`}
        />

        <h3
          className={`text-sm font-semibold text-center mb-4 uppercase tracking-wider ${
            darkMode ? "text-gray-500" : "text-gray-400"
          }`}
        >
          Pendiente
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <div
            className={`text-center rounded-2xl p-4 border ${
              darkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-gray-50 border-gray-100"
            }`}
          >
            <span
              className={`text-xs font-semibold block mb-2 uppercase tracking-wide ${
                darkMode ? "text-gray-500" : "text-gray-400"
              }`}
            >
              Capital
            </span>
            <span className="text-2xl font-black text-green-600">
              {formatCurrency(pendingCapital)}
            </span>
          </div>

          <div
            className={`text-center rounded-2xl p-4 border ${
              darkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-gray-50 border-gray-100"
            }`}
          >
            <span
              className={`text-xs font-semibold block mb-2 uppercase tracking-wide ${
                darkMode ? "text-gray-500" : "text-gray-400"
              }`}
            >
              Capital + Rédito
            </span>
            <span
              className={`text-xl font-medium ${
                darkMode ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {formatCurrency(adjustedPendingBalance)}
            </span>
          </div>
        </div>
      </div>

      <div
        className={`shadow-lg rounded-3xl overflow-hidden border ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`}
      >
        <div
          className={`p-4 font-black text-lg ${
            darkMode ? "bg-gray-700 text-white" : "bg-gray-900 text-white"
          }`}
        >
          Calendario
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className={darkMode ? "bg-gray-700" : "bg-gray-50"}>
              <tr>
                <th
                  className={`px-4 py-3 text-left text-xs font-black uppercase tracking-wider ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {periodLabel}
                </th>
                <th
                  className={`px-4 py-3 text-left text-xs font-black uppercase tracking-wider ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Vencimiento
                </th>
                <th
                  className={`px-4 py-3 text-center text-xs font-black uppercase tracking-wider ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody
              className={`divide-y ${
                darkMode
                  ? "bg-gray-800 divide-gray-700"
                  : "bg-white divide-gray-100"
              }`}
            >
              {[...payments]
                .sort((a, b) => Number(a.week) - Number(b.week))
                .map((p, idx) => {
                  const refinanced = isRefinanced(p);

                  // Verificar si este pago fue directo a capital (sin intereses)
                  const isCapitalOnly = p.paymentHistory?.some((h) =>
                    h.note?.includes(
                      "Pago directo a capital, no aplica intereses.",
                    ),
                  );

                  // Si es pago directo a capital, calcular remaining contra capital solamente
                  const effectiveExpected = isCapitalOnly
                    ? capitalPerPeriod
                    : p.amount || 0;
                  const remaining = effectiveExpected - (p.totalPaid || 0);

                  return (
                    <tr
                      key={p.id}
                      className={`transition-colors duration-200 ${
                        idx % 2 === 1
                          ? darkMode
                            ? "bg-gray-700/30"
                            : "bg-gray-50"
                          : ""
                      } ${
                        darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm font-bold ${
                              darkMode ? "text-gray-200" : "text-gray-900"
                            }`}
                          >
                            {p.week}
                          </span>
                          <div className="flex items-center gap-1">
                            {getPaymentBadges(p).map((badge, idx) => (
                              <span
                                key={idx}
                                className={`w-3 h-3 rounded-full cursor-pointer ${badge.color}`}
                                title={badge.label}
                                onClick={() => setActiveBadge(badge)}
                              />
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {formatDate(p.dueDate)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleOpenDetailsModal(p)}
                            className="p-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 shadow-sm hover:shadow-md transition-all duration-200"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!readOnly && !refinanced && remaining > 0 ? (
                            <button
                              onClick={() => handleOpenPaymentModal(p)}
                              className="p-2 bg-gray-800 text-white rounded-xl shadow-md hover:bg-gray-900 hover:shadow-lg transition-all duration-200"
                              title="Registrar pago"
                            >
                              <Wallet className="w-4 h-4" />
                            </button>
                          ) : (
                            <div className="w-10 h-10" />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {detailsModalOpen && selectedPayment && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div
            className={`p-6 rounded-2xl shadow-2xl animate-scaleIn w-full max-w-md max-h-[90vh] overflow-y-auto border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className={`text-xl font-bold ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                {periodLabel} {selectedPayment.week}
              </h3>
              <button
                onClick={() => setDetailsModalOpen(false)}
                className={
                  darkMode
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-400 hover:text-gray-700"
                }
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div
                className={`border-b pb-3 ${darkMode ? "border-gray-700" : ""}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {getPaymentBadges(selectedPayment).map((badge, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <span className={`w-3 h-3 rounded-full ${badge.color}`} />
                      <span
                        className={`text-sm font-semibold ${
                          darkMode ? "text-gray-200" : "text-gray-800"
                        }`}
                      >
                        {badge.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {(() => {
                const isCapitalOnly = selectedPayment.paymentHistory?.some(
                  (h) =>
                    h.note?.includes(
                      "Pago directo a capital, no aplica intereses.",
                    ),
                );
                const effectiveExpected = isCapitalOnly
                  ? capitalPerPeriod
                  : selectedPayment.amount || 0;
                const pending = Math.max(
                  0,
                  effectiveExpected - (selectedPayment.totalPaid || 0),
                );
                const refinanced = isRefinanced(selectedPayment);
                return (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span
                        className={darkMode ? "text-gray-400" : "text-gray-600"}
                      >
                        Esperado:
                      </span>
                      <span
                        className={`font-semibold ${
                          darkMode ? "text-white" : ""
                        }`}
                      >
                        {formatCurrency(selectedPayment.amount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span
                        className={darkMode ? "text-gray-400" : "text-gray-600"}
                      >
                        Pagado:
                      </span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(selectedPayment.totalPaid || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span
                        className={darkMode ? "text-gray-400" : "text-gray-600"}
                      >
                        {refinanced ? "Refinanciado:" : "Pendiente:"}
                      </span>
                      <span
                        className={`font-semibold ${
                          refinanced
                            ? "text-violet-600"
                            : pending > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {formatCurrency(pending)}
                      </span>
                    </div>
                  </div>
                );
              })()}

              {selectedPayment.note && (
                <div
                  className={`text-xs italic ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  "{selectedPayment.note}"
                </div>
              )}

              {selectedPayment.paymentHistory &&
                selectedPayment.paymentHistory.length > 0 && (
                  <div
                    className={`border-t pt-3 ${
                      darkMode ? "border-gray-700" : ""
                    }`}
                  >
                    <p
                      className={`font-semibold mb-2 text-sm ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Historial:
                    </p>
                    {(() => {
                      const hist = selectedPayment.paymentHistory;
                      const lastPosIdx = hist.reduce(
                        (acc, h, i) => (h.amount > 0 ? i : acc),
                        -1,
                      );
                      const lastPosIsCapitalAdvance =
                        lastPosIdx >= 0 &&
                        (hist[lastPosIdx].note?.includes("Abono a capital") ||
                          hist[lastPosIdx].note?.includes(
                            "Pago directo a capital",
                          ));
                      const hasReversalAfterLastPos =
                        lastPosIdx >= 0 &&
                        hist.slice(lastPosIdx + 1).some((h) => h.amount < 0);
                      return (
                        <>
                          <ul className="space-y-1 max-h-40 overflow-y-auto">
                            {hist.map((h, idx) => {
                              const isReversal = h.amount < 0;
                              const isLastRevertible =
                                !readOnly &&
                                idx === lastPosIdx &&
                                !lastPosIsCapitalAdvance &&
                                !hasReversalAfterLastPos &&
                                selectedPayment.id === globallyRevertiblePaymentId;
                              return (
                                <li
                                  key={idx}
                                  className={`text-sm p-2 rounded ${
                                    isReversal
                                      ? darkMode
                                        ? "text-red-400 bg-gray-700"
                                        : "text-red-600 bg-red-50"
                                      : darkMode
                                      ? "text-gray-300 bg-gray-700"
                                      : "text-gray-600 bg-gray-50"
                                  }`}
                                >
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 flex-shrink-0" />
                                    {formatDate(h.date)} -{formatCurrency(Math.abs(h.amount))}
                                    {h.note
                                      ? ` ${h.note.toLowerCase()}`
                                      : ""}
                                    <span className="ml-auto flex items-center gap-1">
                                      {h.receiptKey && (
                                        <button
                                          onClick={() =>
                                            handleViewReceipt(h.receiptKey)
                                          }
                                          className="text-blue-400 hover:text-blue-300 p-0.5"
                                          title="Ver comprobante"
                                        >
                                          <Image className="w-4 h-4" />
                                        </button>
                                      )}
                                      {isLastRevertible && (
                                        <button
                                          onClick={() =>
                                            handleRevert(selectedPayment)
                                          }
                                          className="text-xs font-semibold text-red-500 hover:text-red-400 px-1 py-0.5 rounded hover:bg-red-50 transition-colors"
                                          title="Revertir este pago"
                                        >
                                          Revertir
                                        </button>
                                      )}
                                    </span>
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                          {!readOnly && lastPosIsCapitalAdvance && (
                            <p
                              className={`text-xs mt-1 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Los pagos de capital adelantado no se pueden
                              revertir aún.
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

              {!readOnly &&
                !isRefinanced(selectedPayment) &&
                isOverdue(selectedPayment) &&
                (selectedPayment.amount || 0) -
                  (selectedPayment.totalPaid || 0) >
                  0 && (
                  <div
                    className={`border-t pt-3 ${
                      darkMode ? "border-gray-700" : ""
                    }`}
                  >
                    <button
                      onClick={() => handleRefinance(selectedPayment.id)}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-orange-600 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                    >
                      {actionLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />{" "}
                          Procesando...
                        </>
                      ) : (
                        "Refinanciar"
                      )}
                    </button>
                    <p
                      className={`text-xs mt-2 text-center ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Moverá el saldo pendiente a una nueva{" "}
                      {periodLabel.toLowerCase()}
                    </p>
                  </div>
                )}
            </div>

            <div
              className={`flex justify-end mt-6 border-t pt-4 ${
                darkMode ? "border-gray-700" : ""
              }`}
            >
              <button
                onClick={() => setDetailsModalOpen(false)}
                className={`px-4 py-2 rounded-lg transition ${
                  darkMode
                    ? "text-red-400 bg-gray-700 hover:bg-gray-600"
                    : "text-red-600 bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {paymentModalOpen && selectedPayment && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div
            className={`p-6 rounded-2xl shadow-2xl animate-scaleIn w-full max-w-md border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <h3
              className={`text-xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              {periodLabel} {selectedPayment.week}
            </h3>
            <div className="space-y-4 mb-6">
              <div
                className={`flex justify-between text-sm border-b pb-2 ${
                  darkMode ? "border-gray-700" : ""
                }`}
              >
                <span className={darkMode ? "text-gray-400" : "text-gray-600"}>
                  Total:
                </span>
                <span
                  className={`font-semibold ${darkMode ? "text-white" : ""}`}
                >
                  {formatCurrency(selectedPayment.amount)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex gap-2">
                  <span
                    className={darkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Pagado:
                  </span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(selectedPayment.totalPaid || 0)}
                  </span>
                </div>
                <div className="flex gap-2 justify-end">
                  <span
                    className={darkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Resta:
                  </span>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(
                      (selectedPayment.amount || 0) -
                        (selectedPayment.totalPaid || 0),
                    )}
                  </span>
                </div>
              </div>

              <div
                className={`border-t pt-4 ${darkMode ? "border-gray-700" : ""}`}
              >
                <p
                  className={`text-sm font-medium mb-3 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Tipo de pago:
                </p>
                <div className="space-y-2">
                  {(() => {
                    const remainingAmount =
                      (selectedPayment.amount || 0) -
                      (selectedPayment.totalPaid || 0);
                    const interestAlreadyPaid = Math.min(
                      selectedPayment.totalPaid || 0,
                      interestPerPeriod,
                    );
                    const remainingInterest = Math.max(
                      0,
                      interestPerPeriod - interestAlreadyPaid,
                    );
                    const interestFullyPaid = remainingInterest <= 0;

                    const isPaymentEffectivelyPaid = (p) => {
                      if (!p) return true;
                      if ((p.totalPaid || 0) >= (p.amount || 0)) return true;
                      if (
                        payments.some(
                          (pay) =>
                            pay.isRefinanced && pay.originalWeek === p.week,
                        )
                      )
                        return true;
                      const isCapitalOnly = p.paymentHistory?.some((h) =>
                        h.note?.includes("Pago directo a capital"),
                      );
                      if (
                        isCapitalOnly &&
                        (p.totalPaid || 0) >= capitalPerPeriod
                      )
                        return true;
                      return false;
                    };

                    const currentWeekNum = Number(selectedPayment.week);
                    const previousPayment = payments.find(
                      (p) => Number(p.week) === currentWeekNum - 1,
                    );
                    const previousPaymentOk =
                      isPaymentEffectivelyPaid(previousPayment);
                    const hasUnpaidLaterWeeks = payments.some(
                      (p) =>
                        Number(p.week) > currentWeekNum &&
                        !isPaymentEffectivelyPaid(p),
                    );
                    const capitalPaymentEnabled =
                      previousPaymentOk && hasUnpaidLaterWeeks;
                    const activeBorder = darkMode ? "#9ca3af" : "#1f2937";
                    const inactiveBorder = darkMode ? "#4b5563" : "#e5e7eb";

                    return (
                      <>
                        <label
                          className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition ${
                            darkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                          }`}
                          style={{
                            borderColor:
                              paymentType === "full"
                                ? activeBorder
                                : inactiveBorder,
                          }}
                        >
                          <input
                            type="radio"
                            name="paymentType"
                            value="full"
                            checked={paymentType === "full"}
                            onChange={(e) => setPaymentType(e.target.value)}
                            className="w-4 h-4"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex justify-between items-center">
                              <span
                                className={`font-semibold ${
                                  darkMode ? "text-white" : "text-gray-800"
                                }`}
                              >
                                {interestFullyPaid
                                  ? "Pagar Capital Restante"
                                  : "Pago Completo"}
                              </span>
                              <span
                                className={`font-bold ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                {formatCurrency(remainingAmount)}
                              </span>
                            </div>
                            <p
                              className={`text-xs ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {interestFullyPaid
                                ? "Solo capital pendiente"
                                : "Capital + Rédito"}
                            </p>
                          </div>
                        </label>

                        <label
                          className={`flex items-center p-3 border-2 rounded-lg transition ${
                            interestFullyPaid
                              ? `opacity-50 cursor-not-allowed ${
                                  darkMode ? "bg-gray-700" : "bg-gray-50"
                                }`
                              : `cursor-pointer ${
                                  darkMode
                                    ? "hover:bg-gray-700"
                                    : "hover:bg-gray-50"
                                }`
                          }`}
                          style={{
                            borderColor:
                              paymentType === "interest" && !interestFullyPaid
                                ? activeBorder
                                : inactiveBorder,
                          }}
                        >
                          <input
                            type="radio"
                            name="paymentType"
                            value="interest"
                            checked={paymentType === "interest"}
                            onChange={(e) => setPaymentType(e.target.value)}
                            className="w-4 h-4"
                            disabled={interestFullyPaid}
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex justify-between items-center">
                              <span
                                className={`font-semibold ${
                                  darkMode ? "text-white" : "text-gray-800"
                                }`}
                              >
                                Solo Rédito
                              </span>
                              <span
                                className={`font-bold ${
                                  interestFullyPaid
                                    ? "text-gray-400"
                                    : "text-orange-500"
                                }`}
                              >
                                {formatCurrency(remainingInterest)}
                              </span>
                            </div>
                            <p
                              className={`text-xs ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {interestFullyPaid
                                ? "Ya pagado"
                                : "Pago de intereses únicamente"}
                            </p>
                          </div>
                        </label>

                        <label
                          className={`flex items-center p-3 border-2 rounded-lg transition ${
                            capitalPaymentEnabled
                              ? `cursor-pointer ${
                                  darkMode
                                    ? "hover:bg-gray-700"
                                    : "hover:bg-gray-50"
                                }`
                              : `opacity-50 cursor-not-allowed ${
                                  darkMode ? "bg-gray-700" : "bg-gray-50"
                                }`
                          }`}
                          style={{
                            borderColor:
                              paymentType === "capital" && capitalPaymentEnabled
                                ? activeBorder
                                : inactiveBorder,
                          }}
                        >
                          <input
                            type="radio"
                            name="paymentType"
                            value="capital"
                            checked={paymentType === "capital"}
                            onChange={(e) => setPaymentType(e.target.value)}
                            className="w-4 h-4"
                            disabled={!capitalPaymentEnabled}
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex justify-between items-center">
                              <span
                                className={`font-semibold ${
                                  capitalPaymentEnabled
                                    ? darkMode
                                      ? "text-white"
                                      : "text-gray-800"
                                    : darkMode
                                    ? "text-gray-500"
                                    : "text-gray-500"
                                }`}
                              >
                                Abono a Capital
                              </span>
                              <span
                                className={`font-bold ${
                                  capitalPaymentEnabled
                                    ? "text-blue-600"
                                    : "text-gray-400"
                                }`}
                              >
                                +
                              </span>
                            </div>
                            <p
                              className={`text-xs ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {capitalPaymentEnabled
                                ? "Pago completo + abono para reducir tiempo"
                                : !hasUnpaidLaterWeeks
                                ? "No hay cuotas posteriores"
                                : "Pagar cuota pendiente para poder abonar"}
                            </p>
                          </div>
                        </label>
                      </>
                    );
                  })()}
                </div>

                {paymentType === "capital" && (
                  <div
                    className={`mt-3 p-3 rounded-lg border-2 border-green-500 ${
                      darkMode ? "bg-gray-700" : "bg-white"
                    }`}
                  >
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Pagar hasta la {periodLabel.toLowerCase()}:
                    </label>
                    <select
                      value={capitalAmount}
                      onChange={(e) => setCapitalAmount(e.target.value)}
                      className={`w-full border-2 border-blue-300 rounded-xl p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm font-medium ${
                        darkMode ? "bg-gray-600 text-white" : "bg-white"
                      }`}
                    >
                      <option value="">
                        Selecciona una {periodLabel.toLowerCase()}
                      </option>
                      {(() => {
                        const currentWeek = Number(selectedPayment.week);
                        const unpaidWeeks = payments
                          .filter(
                            (p) =>
                              Number(p.week) > currentWeek &&
                              (p.totalPaid || 0) < (p.amount || 0),
                          )
                          .sort((a, b) => Number(a.week) - Number(b.week));

                        return unpaidWeeks.map((payment) => {
                          const additionalWeeks =
                            Number(payment.week) - currentWeek;
                          const totalCost =
                            selectedPayment.amount +
                            additionalWeeks * capitalPerPeriod;

                          return (
                            <option key={payment.week} value={payment.week}>
                              {periodLabel} {payment.week} -{" "}
                              {formatCurrency(totalCost)}
                            </option>
                          );
                        });
                      })()}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <textarea
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="Agrega un comentario opcional ..."
                  rows="2"
                  className={`w-full border-2 rounded-xl p-2.5 focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 text-sm font-medium ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 hover:border-gray-500"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                />
              </div>

              {/* Receipt file chooser */}
              <div>
                <input
                  ref={receiptInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                  onChange={handleReceiptSelect}
                  className="hidden"
                />
                {receiptPreview ? (
                  <div
                    className={`flex items-center gap-3 p-2 rounded-lg border ${
                      darkMode
                        ? "border-gray-600 bg-gray-700"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <img
                      src={receiptPreview}
                      alt="Comprobante"
                      className="w-12 h-12 object-cover rounded"
                    />
                    <span
                      className={`text-xs flex-1 truncate ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {receiptFile?.name}
                    </span>
                    <button
                      onClick={clearReceipt}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => receiptInputRef.current?.click()}
                    className={`w-full flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-lg border-2 border-dashed transition-all ${
                      darkMode
                        ? "border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300"
                        : "border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <Paperclip className="w-4 h-4" />+ Comprobante
                  </button>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setPaymentModalOpen(false);
                  setSelectedPayment(null);
                  setPaymentType("full");
                  setPaymentNote("");
                  setCapitalAmount("");
                  clearReceipt();
                }}
                className={`px-4 py-2 rounded-xl hover:scale-105 transition-all duration-200 font-semibold ${
                  darkMode
                    ? "text-red-400 bg-gray-700 hover:bg-gray-600"
                    : "text-red-600 bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleRegisterPayment}
                disabled={actionLoading}
                className={`px-4 py-2 rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2 ${
                  darkMode
                    ? "bg-gray-600 text-green-400 hover:bg-gray-500"
                    : "bg-gray-800 text-green-400 hover:bg-gray-900"
                }`}
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Procesando...
                  </>
                ) : (
                  "Pagar"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación para Abono a Capital */}
      {confirmModalOpen && confirmData && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div
            className={`p-6 rounded-2xl shadow-2xl animate-scaleIn w-full max-w-md border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
          >
            <div className="flex items-center mb-4">
              <div
                className={`p-2 rounded-full mr-3 ${
                  darkMode ? "bg-green-900/40" : "bg-green-100"
                }`}
              >
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3
                className={`text-xl font-bold ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Abono a Capital
              </h3>
            </div>

            <div className="space-y-3 mb-6">
              <div
                className={`p-4 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {periodLabel} actual ({confirmData.currentWeek}):
                  </span>
                  <span
                    className={`font-bold ${
                      darkMode ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {formatCurrency(confirmData.currentWeekAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {confirmData.additionalWeeks}{" "}
                    {periodLabelPlural.toLowerCase()} adicionales (
                    {confirmData.currentWeek + 1} a {confirmData.untilWeek}):
                  </span>
                  <span
                    className={`font-bold ${
                      darkMode ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {formatCurrency(confirmData.additionalAmount)}
                  </span>
                </div>
                <p
                  className={`text-xs mt-2 italic ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Solo capital, sin intereses
                </p>
              </div>

              <div
                className={`p-4 rounded-lg border-2 ${
                  darkMode
                    ? "bg-green-900/20 border-green-700"
                    : "bg-green-50 border-green-200"
                }`}
              >
                <div className="flex justify-between items-center">
                  <span
                    className={`text-sm font-semibold ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Total a pagar:
                  </span>
                  <span className="text-2xl font-black text-green-600">
                    {formatCurrency(confirmData.totalAmount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setConfirmModalOpen(false);
                  setConfirmData(null);
                }}
                className={`px-4 py-2 rounded-xl hover:scale-105 transition-all duration-200 font-semibold ${
                  darkMode
                    ? "text-red-400 bg-gray-700 hover:bg-gray-600"
                    : "text-red-600 bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmCapital}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-xl shadow-md hover:bg-green-700 hover:shadow-lg hover:scale-105 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Procesando...
                  </>
                ) : (
                  "Confirmar Pago"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {receiptImageUrl && (
        <div
          className="fixed inset-0 bg-gray-900/80 flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setReceiptImageUrl(null)}
        >
          <div
            className="relative max-w-lg w-full animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setReceiptImageUrl(null)}
              className="absolute top-3 right-3 z-10 bg-black/50 text-white rounded-full p-2 backdrop-blur-sm"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={receiptImageUrl}
              alt="Comprobante"
              className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}

      {activeBadge && (
        <div
          className="fixed inset-0 bg-gray-900/30 flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setActiveBadge(null)}
        >
          <div
            className={`px-5 py-4 rounded-2xl shadow-2xl animate-scaleIn border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <span
                className={`w-3.5 h-3.5 rounded-full ${activeBadge.color}`}
              />
              <span
                className={`text-sm font-semibold ${
                  darkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                {activeBadge.label}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LoanModal = ({
  loan,
  isOpen,
  onClose,
  onSave,
  darkMode,
  actionLoading,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [loanPrincipal, setLoanPrincipal] = useState(1000);
  const [quota, setQuota] = useState(130);
  const [durationWeeks, setDurationWeeks] = useState(10);
  const [startDate, setStartDate] = useState("");
  const [paymentFrequency, setPaymentFrequency] = useState("weekly");
  const [detailAlt, setDetailAlt] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    if (loan) {
      setName(loan.name || "");
      setDescription(loan.description || "");
      setPhone(loan.phone || "");
      setLoanPrincipal(loan.loanPrincipal || 1000);
      setQuota(loan.paymentAmount || 130);
      setDurationWeeks(loan.durationWeeks || 10);
      setStartDate(loan.startDate || today);
      setPaymentFrequency(loan.paymentFrequency || "weekly");
    } else {
      setName("");
      setDescription("");
      setPhone("");
      setLoanPrincipal(1000);
      setQuota(130);
      setDurationWeeks(10);
      setStartDate(today);
      setPaymentFrequency("weekly");
    }
  }, [loan, isOpen]);

  // Función para formatear WhatsApp mientras escribe
  const formatWhatsApp = (value) => {
    // Eliminar todo excepto dígitos
    const digits = value.replace(/\D/g, "");

    // Limitar a 11 dígitos (1 + 10)
    const limited = digits.substring(0, 11);

    // Formatear según la cantidad de dígitos
    if (limited.length === 0) return "";
    if (limited.length <= 1) return `+${limited}`;
    if (limited.length <= 4) return `+${limited[0]} (${limited.substring(1)}`;
    if (limited.length <= 7)
      return `+${limited[0]} (${limited.substring(1, 4)}) ${limited.substring(
        4,
      )}`;
    return `+${limited[0]} (${limited.substring(1, 4)}) ${limited.substring(
      4,
      7,
    )}-${limited.substring(7)}`;
  };

  // Validaciones
  const isLocked =
    !!loan &&
    (loan.payments || []).some(
      (p) => (p.totalPaid || 0) > 0 || p.isRefinanced === true,
    );

  const isNameValid = name.length <= 25;
  const isPhoneValid = phone.length <= 17;
  const isDescriptionValid = description.length <= 120;
  const isLoanPrincipalValid =
    loanPrincipal > 0 && loanPrincipal <= 10000 && loanPrincipal % 1 === 0;
  const isQuotaValid = quota > 0 && quota <= 3000 && quota % 1 === 0;
  const isDurationValid =
    durationWeeks > 0 && durationWeeks <= 100 && durationWeeks % 1 === 0;

  const totalRepayable = quota * durationWeeks;
  const revenue = totalRepayable - loanPrincipal;

  const capitalPerCuota = durationWeeks > 0 ? loanPrincipal / durationWeeks : 0;
  const interestPerCuota = quota - capitalPerCuota;
  const isCapitalDecimal = durationWeeks > 0 && capitalPerCuota % 1 !== 0;
  const isInterestDecimal = durationWeeks > 0 && interestPerCuota % 1 !== 0;
  const isDetailInvalid =
    isCapitalDecimal ||
    isInterestDecimal ||
    capitalPerCuota < 0 ||
    interestPerCuota < 0;

  const handleSubmit = () => {
    // Validar todos los campos
    if (
      !name ||
      loanPrincipal <= 0 ||
      quota <= 0 ||
      durationWeeks <= 0 ||
      !startDate
    ) {
      return;
    }

    // Validar límites y decimales
    if (
      !isNameValid ||
      !isPhoneValid ||
      !isDescriptionValid ||
      !isLoanPrincipalValid ||
      !isQuotaValid ||
      !isDurationValid ||
      isDetailInvalid
    ) {
      return;
    }
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    onSave({
      id: loan?.id,
      name,
      description,
      phone,
      loanPrincipal,
      quota,
      durationWeeks,
      startDate,
      totalRepayable,
      paymentAmount: quota,
      paymentFrequency,
      createdAt: loan?.createdAt || localDate, // Mantener fecha original si existe, o crear nueva en formato local
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`p-5 rounded-2xl shadow-2xl animate-scaleIn w-full max-w-lg max-h-[90vh] overflow-y-auto border ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`}
      >
        <div className="flex justify-between items-center mb-3 border-b pb-2">
          <h3
            className={`text-xl font-bold ${
              darkMode ? "text-white" : "text-gray-800"
            }`}
          >
            {loan ? "Editar Préstamo" : "Nuevo Préstamo"}
          </h3>
          <button
            onClick={onClose}
            className={
              darkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-400 hover:text-gray-700"
            }
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div>
          <div className="grid grid-cols-1 overflow-hidden gap-3 mb-3">
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Nombre
              </span>
              <input
                type="text"
                required
                maxLength={25}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre completo. E.j. Juan Lopez"
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 transition-all duration-200 font-medium ${
                  !isNameValid
                    ? "border-red-500 focus:border-red-500"
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500 focus:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-gray-500"
                }`}
              />
            </label>
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                WhatsApp
              </span>
              <input
                type="tel"
                maxLength={17}
                value={phone}
                onChange={(e) => setPhone(formatWhatsApp(e.target.value))}
                placeholder="+1 (347) 250-9856"
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 transition-all duration-200 font-medium ${
                  !isPhoneValid
                    ? "border-red-500 focus:border-red-500"
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500 focus:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-gray-500"
                }`}
              />
            </label>
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Nota
              </span>
              <input
                type="text"
                required
                maxLength={120}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Agrega una nota ..."
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 transition-all duration-200 font-medium ${
                  !isDescriptionValid
                    ? "border-red-500 focus:border-red-500"
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500 focus:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-gray-500"
                }`}
              />
            </label>
          </div>
          <br />
          <hr />
          <br />
          <div className="grid grid-cols-2 overflow-hidden gap-3 mb-3">
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Préstamo ($)
              </span>
              <input
                type="number"
                min="1"
                max="10000"
                step="1"
                required
                disabled={isLocked}
                value={loanPrincipal}
                onChange={(e) => setLoanPrincipal(Number(e.target.value))}
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 transition-all duration-200 font-medium ${
                  isLocked
                    ? "opacity-50 cursor-not-allowed " +
                      (darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-400"
                        : "bg-gray-100 border-gray-200 text-gray-500")
                    : !isLoanPrincipalValid
                    ? "border-red-500 focus:border-red-500"
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500 focus:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-gray-500"
                }`}
              />
            </label>
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Cuota ($)
              </span>
              <input
                type="number"
                min="1"
                max="3000"
                step="1"
                required
                disabled={isLocked}
                value={quota}
                onChange={(e) => setQuota(Number(e.target.value))}
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 transition-all duration-200 font-medium ${
                  isLocked
                    ? "opacity-50 cursor-not-allowed " +
                      (darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-400"
                        : "bg-gray-100 border-gray-200 text-gray-500")
                    : !isQuotaValid
                    ? "border-red-500 focus:border-red-500"
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500 focus:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-gray-500"
                }`}
              />
            </label>
          </div>

          {/* Cuotas y Cuota Detallada */}
          <div className="grid grid-cols-2 overflow-hidden gap-3 mb-3">
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Cuotas
              </span>
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                required
                disabled={isLocked}
                value={durationWeeks}
                onChange={(e) => setDurationWeeks(Number(e.target.value))}
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 transition-all duration-200 font-medium ${
                  isLocked
                    ? "opacity-50 cursor-not-allowed " +
                      (darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-400"
                        : "bg-gray-100 border-gray-200 text-gray-500")
                    : !isDurationValid
                    ? "border-red-500 focus:border-red-500"
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500 focus:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300 focus:border-gray-500"
                }`}
              />
            </label>
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Detalle
              </span>
              <input
                type="text"
                readOnly
                onClick={() => !isDetailInvalid && setDetailAlt(!detailAlt)}
                value={
                  durationWeeks > 0
                    ? detailAlt && !isDetailInvalid
                      ? `${quota} x ${durationWeeks} = ${quota * durationWeeks}`
                      : `${Math.round(capitalPerCuota * 100) / 100} + ${
                          Math.round(interestPerCuota * 100) / 100
                        } = ${
                          Math.round(capitalPerCuota * 100) / 100 +
                          Math.round(interestPerCuota * 100) / 100
                        }`
                    : "—"
                }
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal font-medium ${
                  isDetailInvalid
                    ? "border-red-500 text-red-600 cursor-default"
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-gray-300 cursor-pointer"
                    : "bg-gray-50 border-gray-200 text-gray-700 cursor-pointer"
                }`}
              />
            </label>
          </div>

          {/* Frecuencia e Inicio de Pago */}
          <div className="grid grid-cols-2 overflow-hidden gap-3 mb-4">
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Frecuencia
              </span>
              <select
                value={paymentFrequency}
                disabled={isLocked}
                onChange={(e) => setPaymentFrequency(e.target.value)}
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 font-medium ${
                  isLocked
                    ? "opacity-50 cursor-not-allowed " +
                      (darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-400"
                        : "bg-gray-100 border-gray-200 text-gray-500")
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300"
                }`}
              >
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensual</option>
              </select>
            </label>
            <label className="block min-w-0 flex-shrink">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Inicio
              </span>
              <input
                type="date"
                required
                disabled={isLocked}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`mt-1 block w-full max-w-full min-w-0 border-2 rounded-xl shadow-sm p-2 h-[42px] leading-normal focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 font-medium ${
                  isLocked
                    ? "opacity-50 cursor-not-allowed " +
                      (darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-400"
                        : "bg-gray-100 border-gray-200 text-gray-500")
                    : darkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                    : "bg-white border-gray-200 text-gray-900 hover:border-gray-300"
                }`}
              />
            </label>
          </div>

          <div className="flex justify-end space-x-3 border-t pt-3">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-xl hover:scale-105 transition-all duration-200 font-semibold ${
                darkMode
                  ? "text-red-400 bg-gray-700 hover:bg-gray-600"
                  : "text-red-600 bg-gray-100 hover:bg-gray-200"
              }`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={actionLoading}
              className={`px-4 py-2 rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2 ${
                darkMode
                  ? "bg-gray-700 text-green-400 hover:bg-gray-600"
                  : "bg-gray-800 text-green-400 hover:bg-gray-900"
              }`}
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
                </>
              ) : loan ? (
                "Guardar Cambios"
              ) : (
                "Guardar"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteModal = ({
  loan,
  isOpen,
  onClose,
  onConfirm,
  darkMode,
  actionLoading,
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`p-6 rounded-2xl shadow-2xl animate-scaleIn w-full max-w-sm border ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`}
      >
        <h3 className="text-xl font-black mb-4 text-red-600">
          Confirmar Eliminación
        </h3>
        <p className={`mb-6 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
          ¿Estás seguro de que deseas eliminar el préstamo de{" "}
          <b className="font-black text-red-600">{loan?.name}</b>?
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-xl hover:scale-105 transition-all duration-200 font-semibold ${
              darkMode
                ? "text-red-400 bg-gray-700 hover:bg-gray-600"
                : "text-red-600 bg-gray-100 hover:bg-gray-200"
            }`}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={actionLoading}
            className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
          >
            {actionLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Eliminando...
              </>
            ) : (
              "Eliminar"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const getRandomTip = (excludeId = null) => {
  const pool = excludeId ? tipsData.filter(t => t.id !== excludeId) : tipsData;
  return pool[Math.floor(Math.random() * pool.length)];
};

const PinLockModal = ({ darkMode, onUnlock, onLogout }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [time, setTime] = useState(new Date());
  const [tip, setTip] = useState(() => getRandomTip());
  const [tipFade, setTipFade] = useState('visible');

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const MAX_PIN = 6;

  const triggerShake = () => {
    setError(true);
    setShake(true);
    setTimeout(() => {
      setShake(false);
      setError(false);
      setPin('');
      // Cambiar tip con fade al fallar el PIN
      setTipFade('hidden');
      setTimeout(() => {
        setTip(prev => getRandomTip(prev.id));
        setTipFade('visible');
      }, 200);
    }, 650);
  };

  const doSubmit = async (pinToCheck) => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await auth.verifyPin(pinToCheck);
      if (result.valid) {
        onUnlock();
      } else {
        triggerShake();
      }
    } catch {
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (digit) => {
    if (shake || loading) return;
    const newPin = pin + digit;
    if (newPin.length > MAX_PIN) return;
    setPin(newPin);
    if (newPin.length === MAX_PIN) doSubmit(newPin);
  };

  const handleDelete = () => {
    if (shake || loading) return;
    setPin(p => p.slice(0, -1));
  };

  const hours = time.getHours().toString().padStart(2, '0');
  const minutes = time.getMinutes().toString().padStart(2, '0');
  const days = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  const dateStr = `${days[time.getDay()]}, ${time.getDate()} ${months[time.getMonth()]}`;

  const bg = darkMode
    ? 'bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950'
    : 'bg-gradient-to-b from-slate-100 via-gray-50 to-slate-100';

  const keyBg = darkMode
    ? 'bg-white/10 text-white hover:bg-white/20 active:bg-white/30'
    : 'bg-white text-gray-900 hover:bg-gray-100 active:bg-gray-200 shadow-sm';

  const iconKeyBg = darkMode
    ? 'text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/20'
    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 active:bg-gray-200';

  const keypad = [['1','2','3'],['4','5','6'],['7','8','9']];

  return (
    <div className={`min-h-screen flex flex-col items-center justify-between py-10 px-6 ${bg}`}>
      {/* Clock */}
      <div className="text-center pt-4">
        <p className={`text-7xl font-thin tabular-nums tracking-tight ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          {hours}:{minutes}
        </p>
        <p className={`text-sm mt-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{dateStr}</p>
        <Lock className={`w-5 h-5 mx-auto mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
      </div>

      {/* Tip + PIN dots */}
      <div className="flex flex-col items-center gap-10 w-full max-w-xs px-2">
        {/* Tip card */}
        {(() => {
          const cat = categoryConfig[tip.category] || categoryConfig.finanzas;
          const colors = categoryColors[cat.color];
          return (
            <div
              className={`w-full p-5 transition-opacity duration-200 border-l-4 ${
                tipFade === 'visible' ? 'opacity-100' : 'opacity-0'
              } ${darkMode ? 'bg-white/8' : 'bg-white/70 shadow-sm'}`}
              style={{
                borderLeftColor: colors.hex,
                ...(shake ? { animation: 'pinShake 0.6s ease' } : {}),
              }}
            >
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {tip.author ? `"${tip.text}"` : tip.text}
              </p>
              {tip.author && (
                <p className={`text-xs mt-2 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  — {tip.author}
                </p>
              )}
            </div>
          );
        })()}

        {/* PIN dots */}
        <div
          className="flex gap-4"
          style={shake ? { animation: 'pinShake 0.6s ease' } : {}}
        >
          {Array.from({ length: MAX_PIN }).map((_, i) => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full border-2 transition-all duration-150 ${
                i < pin.length
                  ? error
                    ? 'bg-red-500 border-red-500 scale-110'
                    : darkMode ? 'bg-white border-white scale-110' : 'bg-gray-800 border-gray-800 scale-110'
                  : darkMode ? 'border-gray-500 bg-transparent' : 'border-gray-400 bg-transparent'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Keypad */}
      <div className="w-full max-w-xs space-y-3">
        {keypad.map((row, ri) => (
          <div key={ri} className="flex justify-center gap-4">
            {row.map(k => (
              <button
                key={k}
                onClick={() => handleKey(k)}
                className={`w-20 h-14 rounded-2xl text-xl font-semibold transition-all duration-100 active:scale-95 ${keyBg}`}
              >
                {k}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-4">
          <button
            onClick={async () => { setLoading(true); await onLogout(); }}
            className={`w-20 h-14 rounded-2xl flex items-center justify-center transition-all duration-100 active:scale-95 ${iconKeyBg}`}
            title="Cerrar sesión"
          >
            <LogOut className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleKey('0')}
            className={`w-20 h-14 rounded-2xl text-xl font-semibold transition-all duration-100 active:scale-95 ${keyBg}`}
          >
            0
          </button>
          <button
            onClick={handleDelete}
            className={`w-20 h-14 rounded-2xl flex items-center justify-center transition-all duration-100 active:scale-95 ${iconKeyBg}`}
            title="Borrar"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
        <div className="flex justify-center pt-2">
          <button
            onClick={() => doSubmit(pin)}
            disabled={pin.length < 2}
            className={`px-10 py-3 rounded-2xl text-sm font-bold transition-all duration-150 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed ${
              darkMode ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-gray-800 text-white hover:bg-gray-900'
            }`}
          >
            Desbloquear
          </button>
        </div>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-blur-sm">
          <Loader2 className="w-10 h-10 animate-spin text-green-400" />
        </div>
      )}

      <style>{`
        @keyframes pinShake {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-10px); }
          30%       { transform: translateX(10px); }
          45%       { transform: translateX(-8px); }
          60%       { transform: translateX(8px); }
          75%       { transform: translateX(-4px); }
          90%       { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
};

const ALL_BOARDS = ["Histórico", "Prestado", "Proyectado", "Cobrado", "Ganancia", "Activos", "Atrasados", "Caja"];

const EditProfileView = ({ user, onSave, darkMode, setActionLoading, hiddenBoards, onUpdateHiddenBoards }) => {
  const [name, setName] = useState(user?.name || "");
  const [companyName, setCompanyName] = useState(user?.companyName || "");
  const [pin, setPin] = useState("");
  const [editingPin, setEditingPin] = useState(false);
  const [error, setError] = useState(null);
  const [showRemovePinConfirm, setShowRemovePinConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localHiddenBoards, setLocalHiddenBoards] = useState(hiddenBoards);

  const boardsChanged = JSON.stringify([...localHiddenBoards].sort()) !== JSON.stringify([...hiddenBoards].sort());

  const hasChanges =
    name.trim() !== (user?.name || "") ||
    companyName.trim() !== (user?.companyName || "") ||
    pin.trim() !== "" ||
    boardsChanged;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const payload = {};
    if (name.trim() !== user?.name) payload.name = name.trim();
    if (companyName.trim() !== (user?.companyName || "")) payload.companyName = companyName.trim();
    if (pin.trim() !== "") {
      if (!/^\d{4,6}$/.test(pin.trim())) {
        setError("El PIN debe tener entre 4 y 6 dígitos");
        return;
      }
      payload.pin = pin.trim();
    }

    setSaving(true);
    setActionLoading(true);
    try {
      if (Object.keys(payload).length > 0) {
        const updated = await auth.updateProfile(payload);
        setPin("");
        setEditingPin(false);
        onSave(updated);
      }
      if (boardsChanged) {
        await onUpdateHiddenBoards(localHiddenBoards);
      }
    } catch (err) {
      setError(err.message || "Error al guardar");
    } finally {
      setSaving(false);
      setActionLoading(false);
    }
  };

  const handleRemovePin = async () => {
    setActionLoading(true);
    try {
      const updated = await auth.updateProfile({ removePin: true });
      setShowRemovePinConfirm(false);
      onSave(updated);
    } catch (err) {
      setError(err.message || "Error al eliminar el PIN");
      setShowRemovePinConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  const inputClass = `w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 ${
    darkMode
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${
    darkMode ? "text-gray-400" : "text-gray-500"
  }`;

  return (
    <div className="p-4 sm:p-6 max-w-md">
      <h2 className={`text-2xl font-black mb-6 ${darkMode ? "text-white" : "text-gray-900"}`}>
        Perfil
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className={labelClass}>Nombre</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
            placeholder="Tu nombre"
          />
        </div>

        <div>
          <label className={labelClass}>Email</label>
          <input
            type="email"
            value={user?.email || ""}
            readOnly
            className={`${inputClass} cursor-default opacity-60`}
          />
        </div>

        <div>
          <label className={labelClass}>Compañía</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className={inputClass}
            placeholder="Nombre de tu compañía"
          />
        </div>

        <div>
          <label className={labelClass}>Boards</label>
          <p className={`text-xs mb-3 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
            Muestra u oculta los indicadores del resumen.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {ALL_BOARDS.map((board) => {
              const isVisible = !localHiddenBoards.includes(board);
              return (
                <label
                  key={board}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${
                    darkMode
                      ? "bg-gray-700/60 hover:bg-gray-700"
                      : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => {
                      setLocalHiddenBoards(prev =>
                        isVisible ? [...prev, board] : prev.filter(b => b !== board)
                      );
                    }}
                    className="w-4 h-4 rounded accent-green-500 cursor-pointer"
                  />
                  <span className={`text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
                    {board}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={labelClass.replace("mb-1.5", "")}>
              PIN de bloqueo
            </label>
            {user?.hasPin && !showRemovePinConfirm && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setEditingPin(v => !v); setPin(""); }}
                  className={`p-1 rounded-lg transition-colors ${darkMode ? "text-gray-500 hover:text-gray-300 hover:bg-gray-700" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                >
                  {editingPin ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingPin(false); setPin(""); setShowRemovePinConfirm(true); }}
                  className={`p-1 rounded-lg transition-colors ${darkMode ? "text-gray-500 hover:text-red-400 hover:bg-gray-700" : "text-gray-400 hover:text-red-500 hover:bg-gray-100"}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          {showRemovePinConfirm ? (
            <div className={`rounded-xl border p-4 space-y-3 ${darkMode ? "border-gray-600 bg-gray-700/50" : "border-gray-200 bg-gray-50"}`}>
              <p className={`text-xs leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                Al eliminar el PIN, la app cerrará sesión automáticamente por inactividad en lugar de bloquear la pantalla.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleRemovePin}
                  className="flex-1 py-2 rounded-lg text-xs font-bold bg-red-500 hover:bg-red-600 text-white transition-colors"
                >
                  Eliminar PIN
                </button>
                <button
                  type="button"
                  onClick={() => setShowRemovePinConfirm(false)}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${darkMode ? "bg-gray-600 hover:bg-gray-500 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (!user?.hasPin || editingPin) ? (
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className={inputClass}
              placeholder={user?.hasPin ? "Agrega un nuevo pin de 4-6 dígitos." : "Agrega un pin de 4-6 dígitos."}
            />
          ) : null}
        </div>

        {error && (
          <p className="text-sm text-red-500 font-medium">{error}</p>
        )}

        <button
          type="submit"
          disabled={!hasChanges || saving}
          className={`w-full py-2.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
            hasChanges && !saving
              ? "bg-green-500 hover:bg-green-400 text-white"
              : darkMode
              ? "bg-gray-700 text-gray-500 cursor-not-allowed"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Guardar cambios"
          )}
        </button>
      </form>
    </div>
  );
};

// ============================================================
// Notes feature helpers & components
// ============================================================

const formatNoteDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return date.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    return "Ayer";
  } else if (diffDays < 7) {
    return date.toLocaleDateString("es-DO", { weekday: "short" });
  } else if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("es-DO", { day: "numeric", month: "short" });
  }
  return date.toLocaleDateString("es-DO", { day: "numeric", month: "short", year: "numeric" });
};

const NotesListView = ({ notes, darkMode, onSelectNote, onNewNote, onDeleteNote }) => {
  const [search, setSearch] = useState("");

  const filtered = notes
    .filter((n) => {
      const q = search.toLowerCase();
      return !q || (n.title || "").toLowerCase().includes(q) || (n.body || "").toLowerCase().includes(q);
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  return (
    <div className="p-4 sm:p-6 flex flex-col min-h-full">
      <div className="flex items-center justify-between mb-5">
        <h2 className={`text-2xl font-black ${darkMode ? "text-white" : "text-gray-900"}`}>
          Notas
        </h2>
      </div>

      {notes.length > 0 && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Buscar notas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full sm:w-72 px-4 py-2.5 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors ${
              darkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
            }`}
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div
          className={`flex-grow flex flex-col items-center justify-center rounded-2xl border py-24 ${
            darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
          }`}
        >
          <StickyNote
            className={`w-12 h-12 mb-3 ${darkMode ? "text-gray-600" : "text-gray-300"}`}
          />
          <p className={`text-lg font-semibold ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
            {search ? "Sin resultados" : "Sin notas aún"}
          </p>
          <p className={`text-sm mt-1 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
            {search ? "Intenta otra búsqueda" : 'Toca "+" para crear tu primera nota'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((note) => (
            <div
              key={note.noteId}
              onClick={() => onSelectNote(note)}
              className={`relative p-4 rounded-2xl border cursor-pointer transition-all duration-200 hover:shadow-md group ${
                darkMode
                  ? "bg-gray-800 border-gray-700 hover:border-gray-500"
                  : "bg-white border-gray-100 hover:border-gray-300 hover:shadow-md"
              }`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteNote(note); }}
                className={`absolute top-3 right-3 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-150 ${
                  darkMode
                    ? "text-gray-500 hover:text-red-400 hover:bg-gray-700"
                    : "text-gray-300 hover:text-red-500 hover:bg-red-50"
                }`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <p className={`text-xs font-medium mb-2 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                {formatNoteDate(note.updatedAt)}
              </p>
              {note.title ? (
                <p className={`font-black text-sm mb-1.5 truncate pr-6 ${darkMode ? "text-white" : "text-gray-900"}`}>
                  {note.title}
                </p>
              ) : null}
              <p className={`text-sm leading-relaxed line-clamp-3 ${darkMode ? "text-gray-400" : "text-gray-500"} ${!note.title ? "font-medium" : ""}`}>
                {note.body || (
                  <span className={`italic ${darkMode ? "text-gray-600" : "text-gray-300"}`}>
                    Nota vacía
                  </span>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={onNewNote}
        className="fixed bottom-8 right-8 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-400 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200 flex items-center justify-center z-20"
      >
        <Plus className="w-7 h-7" />
      </button>
    </div>
  );
};

const NoteEditorView = ({ note, darkMode, onBack, onNoteCreated, onNoteUpdated, onNoteDeleted }) => {
  const [noteId, setNoteId] = useState(note?.noteId || null);
  const [title, setTitle] = useState(note?.title || "");
  const [body, setBody, { undo, redo, reset: resetBody, canUndo, canRedo }] = useUndoRedo(note?.body || "");
  const [lastSavedAt, setLastSavedAt] = useState(note?.updatedAt || null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const noteIdRef = useRef(note?.noteId || null);
  const titleRef = useRef(note?.title || "");
  const bodyRef = useRef(note?.body || "");
  const onNoteCreatedRef = useRef(onNoteCreated);
  const onNoteUpdatedRef = useRef(onNoteUpdated);
  const saveTimeoutRef = useRef(null);
  const saveRef = useRef(null);
  const isDirtyRef = useRef(false);

  useEffect(() => { titleRef.current = title; }, [title]);
  useEffect(() => { bodyRef.current = body; }, [body]);
  useEffect(() => { noteIdRef.current = noteId; }, [noteId]);
  useEffect(() => { onNoteCreatedRef.current = onNoteCreated; }, [onNoteCreated]);
  useEffect(() => { onNoteUpdatedRef.current = onNoteUpdated; }, [onNoteUpdated]);

  const save = async () => {
    const t = titleRef.current;
    const b = bodyRef.current;
    if (!t && !b) return;
    try {
      if (!noteIdRef.current) {
        const created = await notesApi.create({ title: t, body: b });
        setNoteId(created.noteId);
        noteIdRef.current = created.noteId;
        setLastSavedAt(created.updatedAt);
        onNoteCreatedRef.current(created);
      } else {
        const updated = await notesApi.update(noteIdRef.current, { title: t, body: b });
        setLastSavedAt(updated.updatedAt);
        onNoteUpdatedRef.current(updated);
      }
    } catch {
      // silently ignore — user can retry by navigating away
    }
  };

  saveRef.current = save;

  // Fetch fresh note data on mount to avoid stale-cache overwrites from other devices
  useEffect(() => {
    if (!note?.noteId) return;
    let cancelled = false;
    notesApi.get(note.noteId).then((fresh) => {
      if (cancelled || isDirtyRef.current) return;
      setTitle(fresh.title || "");
      titleRef.current = fresh.title || "";
      resetBody(fresh.body || "");
      bodyRef.current = fresh.body || "";
      setLastSavedAt(fresh.updatedAt || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-save (only fires after user actually edits)
  useEffect(() => {
    if (!isDirtyRef.current) return;
    if (!title && !body) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveRef.current(), 1500);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [title, body]);

  const handleBack = () => {
    clearTimeout(saveTimeoutRef.current);
    if (isDirtyRef.current && (titleRef.current || bodyRef.current)) {
      saveRef.current();
    }
    onBack();
  };

  const handleDelete = async () => {
    if (!noteIdRef.current) {
      onBack();
      return;
    }
    setDeleting(true);
    try {
      await notesApi.delete(noteIdRef.current);
      onNoteDeleted(noteIdRef.current);
    } catch {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatEditorDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return (
      date.toLocaleDateString("es-DO", { day: "numeric", month: "long", year: "numeric" }) +
      " a las " +
      date.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" })
    );
  };

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div
        className={`flex items-center gap-1 px-3 py-2 border-b ${
          darkMode ? "border-gray-700" : "border-gray-100"
        }`}
      >
        <button
          onClick={handleBack}
          className={`p-2 rounded-xl transition-colors ${
            darkMode ? "text-amber-400 hover:bg-gray-700" : "text-amber-600 hover:bg-amber-50"
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex-1" />

        <button
          onClick={undo}
          disabled={!canUndo}
          title="Deshacer"
          className={`p-2 rounded-xl transition-all duration-200 ${
            darkMode
              ? "text-gray-400 hover:text-gray-100 hover:bg-gray-700"
              : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          } disabled:opacity-25 disabled:cursor-not-allowed`}
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          onClick={redo}
          disabled={!canRedo}
          title="Rehacer"
          className={`p-2 rounded-xl transition-all duration-200 ${
            darkMode
              ? "text-gray-400 hover:text-gray-100 hover:bg-gray-700"
              : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          } disabled:opacity-25 disabled:cursor-not-allowed`}
        >
          <Redo2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          title="Eliminar nota"
          className={`p-2 rounded-xl transition-all duration-200 ${
            darkMode
              ? "text-gray-500 hover:text-red-400 hover:bg-gray-700"
              : "text-gray-400 hover:text-red-500 hover:bg-red-50"
          }`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Editor area */}
      <div className="flex-1 flex flex-col overflow-hidden px-5 pt-5 pb-4">
        <input
          type="text"
          value={title}
          onChange={(e) => { isDirtyRef.current = true; setTitle(e.target.value); }}
          placeholder="Título"
          maxLength={200}
          className={`w-full text-2xl font-black bg-transparent border-none outline-none mb-2 ${
            darkMode ? "text-white placeholder-gray-600" : "text-gray-900 placeholder-gray-300"
          }`}
        />
        {lastSavedAt && (
          <p className={`text-xs font-medium mb-4 select-none ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
            {formatEditorDate(lastSavedAt)}
          </p>
        )}
        <textarea
          value={body}
          onChange={(e) => { isDirtyRef.current = true; setBody(e.target.value); }}
          placeholder="Empieza a escribir..."
          className={`w-full bg-transparent border-none outline-none resize-none text-base leading-relaxed min-h-[60vh] ${
            darkMode ? "text-gray-200 placeholder-gray-600" : "text-gray-800 placeholder-gray-300"
          }`}
        />
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div
            className={`p-6 rounded-2xl shadow-2xl animate-scaleIn w-full max-w-sm border ${
              darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
            }`}
          >
            <h3 className="text-xl font-black mb-4 text-red-600">Confirmar Eliminación</h3>
            <p className={`mb-6 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              ¿Estás seguro de que deseas eliminar{" "}
              <b className="font-black text-red-600">{title || "esta nota"}</b>?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-200 hover:scale-105 ${
                  darkMode
                    ? "text-red-400 bg-gray-700 hover:bg-gray-600"
                    : "text-red-600 bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
              >
                {deleting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Eliminando...</>
                ) : (
                  "Eliminar"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DeleteNoteModal = ({ note, isOpen, onClose, onConfirm, darkMode, actionLoading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`p-6 rounded-2xl shadow-2xl animate-scaleIn w-full max-w-sm border ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`}
      >
        <h3 className="text-xl font-black mb-4 text-red-600">Confirmar Eliminación</h3>
        <p className={`mb-6 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
          ¿Estás seguro de que deseas eliminar{" "}
          <b className="font-black text-red-600">{note?.title || "esta nota"}</b>?
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-xl hover:scale-105 transition-all duration-200 font-semibold ${
              darkMode
                ? "text-red-400 bg-gray-700 hover:bg-gray-600"
                : "text-red-600 bg-gray-100 hover:bg-gray-200"
            }`}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={actionLoading}
            className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
          >
            {actionLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Eliminando...</>
            ) : (
              "Eliminar"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [loans, setLoans] = useState([]);
  const [activeView, setActiveView] = useState("dashboard");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [safaconSearch, setSafaconSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [filterType, setFilterType] = useState("rating"); // 'rating', 'status', 'date'
  const [ratingFilter, setRatingFilter] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'paid', 'pending'
  const [dateFrom, setDateFrom] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingLoan, setDeletingLoan] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [cajaAmount, setCajaAmount] = useState(0);
  const [favoriteTips, setFavoriteTips] = useState([]);
  const [hiddenBoards, setHiddenBoards] = useState([]);
  const [notes, setNotes] = useState([]);
  const [notesSubView, setNotesSubView] = useState("list");
  const [editingNote, setEditingNote] = useState(null);
  const [deletingNote, setDeletingNote] = useState(null);
  const [deleteNoteModalOpen, setDeleteNoteModalOpen] = useState(false);
  const [noteActionLoading, setNoteActionLoading] = useState(false);

  // Función helper para determinar si un préstamo está completamente pagado
  // Un préstamo está pagado cuando el CAPITAL está cubierto
  // Los intereses son proyectados y pueden no cobrarse por pago anticipado
  const isLoanPaid = (loan) => {
    if (!loan || !loan.payments || loan.payments.length === 0) return false;

    const numPeriods = loan.durationWeeks;
    const capitalPerPeriod = (loan.loanPrincipal || 0) / numPeriods;

    // Calcular cuántas cuotas están completamente pagadas
    const fullPeriodsPaid = loan.payments.filter((p) => {
      // Excluir semanas que fueron refinanciadas (inmutables)
      const wasRefinanced = loan.payments.some(
        (pay) => pay.isRefinanced && pay.originalWeek === p.week,
      );
      if (wasRefinanced) return false;

      // Verificar si fue pago directo a capital (sin intereses)
      const isCapitalOnly = p.paymentHistory?.some((h) =>
        h.note?.includes("Pago directo a capital, no aplica intereses."),
      );

      // Si es pago directo a capital, verificar contra capital solamente
      const effectiveAmount = isCapitalOnly ? capitalPerPeriod : p.amount || 0;

      return (p.totalPaid || 0) >= effectiveAmount;
    }).length;

    const capitalPaid = fullPeriodsPaid * capitalPerPeriod;
    const pendingCapital = (loan.loanPrincipal || 0) - capitalPaid;

    // El préstamo está pagado cuando el capital pendiente es 0 o menos
    return pendingCapital <= 0.01;
  };

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [sessionKickedMsg, setSessionKickedMsg] = useState(null);
  const [pinLocked, setPinLocked] = useState(false);

  // Ref to always access latest user inside async callbacks
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

  // Register session-kicked callback
  useEffect(() => {
    setSessionKickedCallback(() => {
      setSessionKickedMsg("Tu sesión fue cerrada desde otro dispositivo.");
      localStorage.removeItem("user-data");
      setUser(null);
      setIsAuthenticated(false);
      setLoans([]);
      setActiveView("dashboard");
    });
  }, []);

  // Background PIN lock (visibilitychange)
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (userRef.current?.hasPin) {
          localStorage.setItem("app-bg-locked", "1");
        }
      } else if (document.visibilityState === "visible") {
        if (localStorage.getItem("app-bg-locked") === "1") {
          setPinLocked(true);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isAuthenticated]);

  // Inactivity auto-logout timer
  useEffect(() => {
    if (!isAuthenticated) return;
    let timeoutId;
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        if (userRef.current?.hasPin) {
          try { await auth.lock(); } catch { /* best-effort */ }
          sessionStorage.setItem("pin-locked", "1");
          setPinLocked(true);
        } else {
          await auth.logout();
          localStorage.removeItem("user-data");
          setUser(null);
          setIsAuthenticated(false);
          setLoans([]);
          setActiveView("dashboard");
          setSessionKickedMsg("Sesión cerrada por inactividad.");
        }
      }, INACTIVITY_TIMEOUT_MS);
    };
    const events = ["mousemove", "keydown", "touchstart", "click", "scroll"];
    events.forEach((evt) => window.addEventListener(evt, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(timeoutId);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [isAuthenticated]);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const isAuth = auth.isAuthenticated();
        setIsAuthenticated(isAuth);
        if (isAuth) {
          const stored = localStorage.getItem("user-data");
          if (stored) setUser(JSON.parse(stored));
          if (
            sessionStorage.getItem("pin-locked") === "1" ||
            localStorage.getItem("app-bg-locked") === "1"
          ) {
            setPinLocked(true);
          }
        }
      } catch (err) {
        console.error("Auth check failed:", err);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Load loans from API when authenticated
  useEffect(() => {
    const loadLoans = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await loansApi.list();
        // Transform API response to match frontend format
        const transformedLoans = (data || []).map((loan) => ({
          ...loan,
          id: loan.loanId,
          quota: loan.paymentAmount,
          payments: (loan.payments || []).map((p) => ({
            ...p,
            id: p.paymentId,
            amount: p.amount || loan.paymentAmount,
          })),
        }));
        setLoans(transformedLoans);
      } catch (err) {
        console.error("Failed to load loans:", err);
        showMsg("Error al cargar préstamos", true);
      }
    };
    loadLoans();
  }, [isAuthenticated]);

  // Load notes from API when authenticated
  useEffect(() => {
    const loadNotes = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await notesApi.list();
        setNotes(data || []);
      } catch (err) {
        console.error("Failed to load notes:", err);
      }
    };
    loadNotes();
  }, [isAuthenticated]);

  // Load settings: localStorage first (instant), then sync from DynamoDB
  useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    // 1. Load from localStorage cache immediately
    const cachedCaja = localStorage.getItem(`caja-amount-${user.email}`);
    if (cachedCaja !== null) setCajaAmount(Number(cachedCaja));
    const cachedDark = localStorage.getItem(`dark-mode-${user.email}`);
    if (cachedDark !== null) setDarkMode(cachedDark === "true");
    // 2. Sync from DynamoDB (source of truth)
    settingsApi
      .get()
      .then((data) => {
        const remoteCaja = data.cajaAmount || 0;
        const remoteDark = data.darkMode ?? true;
        const remoteFavs = data.favoriteTips || [];
        const remoteHidden = data.hiddenBoards || [];
        setCajaAmount(remoteCaja);
        setDarkMode(remoteDark);
        setFavoriteTips(remoteFavs);
        setHiddenBoards(remoteHidden);
        localStorage.setItem(
          `caja-amount-${user.email}`,
          remoteCaja.toString(),
        );
        localStorage.setItem(`dark-mode-${user.email}`, remoteDark.toString());
      })
      .catch(() => {
        /* use cached values */
      });
  }, [isAuthenticated, user]);

  const updateCaja = (newAmount) => {
    const amount = Math.max(0, Number(newAmount) || 0);
    setCajaAmount(amount);
    if (user?.email) {
      localStorage.setItem(`caja-amount-${user.email}`, amount.toString());
    }
    settingsApi.update({ cajaAmount: amount }).catch(() => {});
  };

  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    if (user?.email) {
      localStorage.setItem(`dark-mode-${user.email}`, newValue.toString());
    }
    settingsApi.update({ darkMode: newValue }).catch(() => {});
  };

  const handleToggleFavorite = (tipId) => {
    setFavoriteTips((prev) => {
      const next = prev.includes(tipId)
        ? prev.filter((id) => id !== tipId)
        : [...prev, tipId];
      settingsApi.update({ favoriteTips: next }).catch(() => {});
      return next;
    });
  };

  const updateHiddenBoards = async (newHiddenBoards) => {
    setHiddenBoards(newHiddenBoards);
    await settingsApi.update({ hiddenBoards: newHiddenBoards }).catch(() => {});
  };

  const showMsg = (text, isError = false) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 3500);
  };

  const handleDeleteNote = async () => {
    if (!deletingNote) return;
    setNoteActionLoading(true);
    try {
      await notesApi.delete(deletingNote.noteId);
      setNotes((prev) => prev.filter((n) => n.noteId !== deletingNote.noteId));
      setDeleteNoteModalOpen(false);
      setDeletingNote(null);
    } catch {
      // silently ignore
    } finally {
      setNoteActionLoading(false);
    }
  };

  // Auth handlers
  const handleLogin = async (email, password) => {
    setSessionKickedMsg(null);
    setPinLocked(false);
    const data = await auth.login(email, password);
    const userData = { email, name: data.user?.name || email.split("@")[0], companyName: data.user?.companyName || "", hasPin: data.user?.hasPin || false };
    setUser(userData);
    localStorage.setItem("user-data", JSON.stringify(userData));
    setIsAuthenticated(true);
  };

  const handleSignup = async (email, password, name, invitationToken) => {
    setSessionKickedMsg(null);
    const data = await auth.signup(email, password, name, invitationToken);
    const userData = { email, name: name || email.split("@")[0] };
    setUser(userData);
    localStorage.setItem("user-data", JSON.stringify(userData));
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    sessionStorage.removeItem("pin-locked");
    localStorage.removeItem("app-bg-locked");
    await auth.logout();
    localStorage.removeItem("user-data");
    setUser(null);
    setIsAuthenticated(false);
    setLoans([]);
    setActiveView("dashboard");
  };

  const getInitials = (name) =>
    name
      ? name.split(/[\s\-_]/).filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2)
      : "?";

  useEffect(() => {
    if (!showUserMenu) return;
    const handleClose = (e) => {
      if (!e.target.closest("[data-user-menu]")) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClose);
    return () => document.removeEventListener("mousedown", handleClose);
  }, [showUserMenu]);

  // Funciones de WhatsApp
  const buildWhatsAppUrl = (phone, message) => {
    const url = new URL("https://api.whatsapp.com/send");
    url.searchParams.set("phone", phone);
    url.searchParams.set("text", message);
    return url.toString();
  };

  const openWhatsApp = (phone, message) => {
    const url = buildWhatsAppUrl(phone, message);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = url;
    } else {
      window.open(url, "_blank");
    }
  };

  const generatePaymentReminder = (loan) => {
    const nextPayment = loan.payments?.find((p) => {
      const remaining = (p.amount || 0) - (p.totalPaid || 0);
      return (
        remaining > 0 &&
        !loan.payments.some(
          (pay) => pay.isRefinanced && pay.originalWeek === p.week,
        )
      );
    });

    if (!nextPayment) return;

    const periodLabel = loan.paymentFrequency === "monthly" ? "cuota" : "cuota";
    const senderName = user?.companyName || user?.name || "Dulus Loans";
    const message = `\u00A1Hola ${loan.name}! \u{1F44B}

Este es un recordatorio de pago de ${senderName} para tu ${periodLabel} #${
      nextPayment.week
    }.

\u{1F4C5} Vencimiento: ${formatDate(nextPayment.dueDate)}
\u{1F4B0} Monto de la cuota: ${formatCurrency(nextPayment.amount)}

\u00A1Gracias por tu confianza!`;

    const phone = loan.phone?.replace(/\D/g, "");
    if (phone) {
      openWhatsApp(phone, message);
    } else {
      showMsg("Este pr\u00E9stamo no tiene n\u00FAmero de tel\u00E9fono", true);
    }
  };

  const generatePaymentReceipt = (
    loan,
    payment,
    amountPaid,
    paymentType,
    capitalUntilWeek,
  ) => {
    const today = new Date().toISOString().split("T")[0];

    // Determine payment type label
    let tipoLabel;
    let cuotaLine;
    if (paymentType === "capital") {
      tipoLabel = "Abono a capital";
      cuotaLine = `\u{1F4CB} Cuotas: ${payment.week} al ${capitalUntilWeek}`;
    } else if (paymentType === "interest") {
      tipoLabel = "Pago Parcial";
      cuotaLine = `\u{1F4CB} Cuota: ${payment.week}`;
    } else {
      tipoLabel = "Pago Completo";
      cuotaLine = `\u{1F4CB} Cuota: ${payment.week}`;
    }

    // Calculate remaining capital after this payment
    const numPeriods = loan.durationWeeks;
    const capitalPerPeriod = (loan.loanPrincipal || 0) / numPeriods;
    const payments = loan.payments || [];
    const fullPeriodsPaid = payments.filter((p) => {
      const wasRefinanced = payments.some(
        (pay) => pay.isRefinanced && pay.originalWeek === p.week,
      );
      if (wasRefinanced) return false;
      const isCapitalOnly = p.paymentHistory?.some((h) =>
        h.note?.includes("Pago directo a capital, no aplica intereses."),
      );
      const effectiveAmount = isCapitalOnly ? capitalPerPeriod : p.amount || 0;
      return (p.totalPaid || 0) >= effectiveAmount;
    }).length;
    const capitalPaid = fullPeriodsPaid * capitalPerPeriod;
    const pendingCapital = Math.max(0, (loan.loanPrincipal || 0) - capitalPaid);

    const receiptSender = user?.companyName || user?.name || "Dulus Loans";
    const message = `\u{1F9FE} RECIBO DE PAGO

\u{1F464} Nombre: ${loan.name}
\u{1F4C5} Fecha: ${formatDate(today)}
${cuotaLine}
\u{1F4B3} Tipo de pago: ${tipoLabel}
\u{1F4B0} Monto pagado: ${formatCurrency(amountPaid)}
\u{1F4CA} Saldo Pendiente: ${formatCurrency(pendingCapital)}

\u2705 Pago registrado exitosamente

${receiptSender}
\u00A1Gracias por tu pago!`;

    const phone = loan.phone?.replace(/\D/g, "");
    if (phone) {
      openWhatsApp(phone, message);
    }
  };

  const payAllPendingWeeks = async (loanId) => {
    if (actionLoading) return;
    const loan = loans.find((l) => l.id === loanId);
    if (!loan) return;

    const periodLabelPlural = "cuotas";
    const pendingPayments = loan.payments.filter((p) => {
      const remaining = (p.amount || 0) - (p.totalPaid || 0);
      return (
        remaining > 0 &&
        !loan.payments.some(
          (pay) => pay.isRefinanced && pay.originalWeek === p.week,
        )
      );
    });

    if (pendingPayments.length === 0) return;

    const totalAmount = pendingPayments.reduce(
      (sum, p) => sum + ((p.amount || 0) - (p.totalPaid || 0)),
      0,
    );

    setActionLoading(true);
    try {
      // Pay all pending payments via API
      let lastPaymentResult;
      for (const p of pendingPayments) {
        const remaining = (p.amount || 0) - (p.totalPaid || 0);
        lastPaymentResult = await paymentsApi.create(loanId, {
          paymentId: p.id,
          amount: remaining,
          note: `Pago total de ${periodLabelPlural} pendientes`,
        });
      }

      // Use the loan returned by the last POST response — all writes are done by
      // this point and the backend queries DynamoDB right after its own write,
      // avoiding the eventual consistency race that a separate GET call would risk
      const updatedLoan = lastPaymentResult.loan;
      const transformedLoan = {
        ...updatedLoan,
        id: updatedLoan.loanId,
        quota: updatedLoan.paymentAmount,
        payments: (updatedLoan.payments || []).map((p) => ({
          ...p,
          id: p.paymentId,
          amount: p.amount || updatedLoan.paymentAmount,
        })),
      };

      setLoans((prev) =>
        prev.map((l) => (l.id === loanId ? transformedLoan : l)),
      );
    } catch (err) {
      console.error("Pay all error:", err);
      showMsg(err.message || "Error al pagar todas las cuotas", true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRatingChange = async (loanId, newRating) => {
    const oldRating = loans.find((l) => l.id === loanId)?.rating || 0;
    setLoans((prev) =>
      prev.map((loan) =>
        loan.id === loanId ? { ...loan, rating: newRating } : loan,
      ),
    );
    try {
      await loansApi.update(loanId, { rating: newRating });
    } catch (err) {
      console.error("Rating update error:", err);
      setLoans((prev) =>
        prev.map((loan) =>
          loan.id === loanId ? { ...loan, rating: oldRating } : loan,
        ),
      );
    }
  };

  const handleSave = async (data) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (data.id) {
        // Update existing loan via API
        const existingLoan = loans.find((l) => l.id === data.id);
        const loanData = {
          name: data.name,
          description: data.description,
          phone: data.phone,
          loanPrincipal: data.loanPrincipal,
          paymentAmount: data.quota,
          durationWeeks: data.durationWeeks,
          paymentFrequency: data.paymentFrequency || "weekly",
          startDate: data.startDate,
          rating: existingLoan?.rating || 0,
        };
        await loansApi.update(data.id, loanData);

        // Ajustar caja si cambió el loanPrincipal
        if (existingLoan && data.loanPrincipal !== undefined && data.loanPrincipal !== existingLoan.loanPrincipal) {
          const diff = data.loanPrincipal - existingLoan.loanPrincipal;
          updateCaja(cajaAmount - diff);
        }

        // Reload loans from API
        const updatedLoans = await loansApi.list();
        const transformedLoans = (updatedLoans || []).map((loan) => ({
          ...loan,
          id: loan.loanId,
          quota: loan.paymentAmount,
          payments: (loan.payments || []).map((p) => ({
            ...p,
            id: p.paymentId,
            amount: p.amount || loan.paymentAmount,
          })),
        }));
        setLoans(transformedLoans);
      } else {
        // Create new loan via API
        const loanData = {
          name: data.name,
          description: data.description,
          phone: data.phone,
          loanPrincipal: data.loanPrincipal,
          paymentAmount: data.quota,
          durationWeeks: data.durationWeeks,
          paymentFrequency: data.paymentFrequency || "weekly",
          startDate: data.startDate,
          rating: data.rating || 0,
        };
        const newLoan = await loansApi.create(loanData);

        // Transform and add to state
        const transformedLoan = {
          ...newLoan,
          id: newLoan.loanId,
          quota: newLoan.paymentAmount,
          payments: (newLoan.payments || []).map((p) => ({
            ...p,
            id: p.paymentId,
            amount: p.amount || newLoan.paymentAmount,
          })),
        };
        setLoans((prev) => [...prev, transformedLoan]);
        updateCaja(cajaAmount - data.loanPrincipal);
        setSelectedLoanId(newLoan.loanId);
        setActiveView("loanDetail");
      }
    } catch (err) {
      console.error("Save loan error:", err);
      showMsg(err.message || "Error al guardar préstamo", true);
    } finally {
      setActionLoading(false);
    }

    setModalOpen(false);
    setEditingLoan(null);
  };

  const handleDelete = async () => {
    if (actionLoading) return;
    if (deletingLoan) {
      setActionLoading(true);
      try {
        await loansApi.update(deletingLoan.id, { status: "deleted" });
        setLoans((prev) =>
          prev.map((l) =>
            l.id === deletingLoan.id ? { ...l, status: "deleted" } : l,
          ),
        );
        if (selectedLoanId === deletingLoan.id) {
          setSelectedLoanId(null);
          setActiveView("dashboard");
        }
      } catch (err) {
        console.error("Delete loan error:", err);
        showMsg(err.message || "Error al eliminar pr\u00E9stamo", true);
      } finally {
        setActionLoading(false);
      }
      setDeleteModalOpen(false);
      setDeletingLoan(null);
    }
  };

  const handlePermanentDelete = async (loan) => {
    if (actionLoading) return;
    if (
      !window.confirm(
        `\u00BFEliminar permanentemente el pr\u00E9stamo de ${loan.name}? Esta acci\u00F3n no se puede deshacer.`,
      )
    )
      return;
    setActionLoading(true);
    try {
      await loansApi.delete(loan.id);
      setLoans((prev) => prev.filter((l) => l.id !== loan.id));
    } catch (err) {
      console.error("Permanent delete error:", err);
      showMsg(err.message || "Error al eliminar pr\u00E9stamo", true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegisterPayment = async (
    loanId,
    paymentId,
    amount,
    note,
    paymentType,
    receiptFile,
  ) => {
    if (actionLoading) return;
    // Confirmación para pagos grandes
    if (amount >= 5000) {
      if (
        !window.confirm(
          `⚠️ CONFIRMAR PAGO GRANDE\n\n¿Estás seguro de registrar un pago de ${formatCurrency(
            amount,
          )}?`,
        )
      ) {
        return;
      }
    }

    const loan = loans.find((l) => l.id === loanId);
    const payment = loan?.payments?.find((p) => p.id === paymentId);

    setActionLoading(true);
    try {
      // Upload receipt to S3 if provided
      let receiptKey = undefined;
      if (receiptFile) {
        try {
          const { uploadUrl, receiptKey: key } = await receiptsApi.getUploadUrl(
            {
              loanId,
              paymentId,
              contentType: receiptFile.type,
            },
          );
          await fetch(uploadUrl, {
            method: "PUT",
            body: receiptFile,
            headers: { "Content-Type": receiptFile.type },
          });
          receiptKey = key;
        } catch (uploadErr) {
          console.error("Receipt upload error:", uploadErr);
          showMsg(
            "Error al subir comprobante, el pago se registrará sin imagen.",
            true,
          );
        }
      }

      // Call API to register payment
      const paymentResult = await paymentsApi.create(loanId, {
        paymentId,
        amount,
        note,
        paymentType,
        receiptKey,
      });

      // Use the loan returned in the POST response to avoid a second round-trip
      // and DynamoDB eventual consistency issues (stale reads after a fresh write)
      const updatedLoan = paymentResult.loan;
      const transformedLoan = {
        ...updatedLoan,
        id: updatedLoan.loanId,
        quota: updatedLoan.paymentAmount,
        payments: (updatedLoan.payments || []).map((p) => ({
          ...p,
          id: p.paymentId,
          amount: p.amount || updatedLoan.paymentAmount,
        })),
      };

      setLoans((prev) =>
        prev.map((l) => (l.id === loanId ? transformedLoan : l)),
      );

      // Enviar recibo por WhatsApp si hay teléfono (use transformedLoan for accurate pending capital)
      if (loan?.phone && payment) {
        setTimeout(() => {
          generatePaymentReceipt(transformedLoan, payment, amount, paymentType);
        }, 500);
      }
    } catch (err) {
      console.error("Payment error:", err);
      showMsg(err.message || "Error al registrar pago", true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCapitalPayment = async (loanId, paymentId, untilWeek, note) => {
    if (actionLoading) return;
    const loan = loans.find((l) => l.id === loanId);
    if (!loan) return;

    setActionLoading(true);
    try {
      // Call API to create capital payment
      const capitalResult = await paymentsApi.createCapitalPayment(loanId, {
        currentPaymentId: paymentId,
        untilWeek,
        note,
      });

      // Use the loan returned in the POST response to avoid a second round-trip
      // and DynamoDB eventual consistency issues (stale reads after a fresh write)
      const updatedLoan = capitalResult.loan;
      const transformedLoan = {
        ...updatedLoan,
        id: updatedLoan.loanId,
        quota: updatedLoan.paymentAmount,
        payments: (updatedLoan.payments || []).map((p) => ({
          ...p,
          id: p.paymentId,
          amount: p.amount || updatedLoan.paymentAmount,
        })),
      };

      setLoans((prev) =>
        prev.map((l) => (l.id === loanId ? transformedLoan : l)),
      );

      // Enviar recibo por WhatsApp si hay teléfono
      const payment = loan.payments?.find((p) => p.id === paymentId);
      if (loan.phone && payment) {
        // Calculate total amount: current week full + additional weeks capital only
        const numPeriods = loan.durationWeeks;
        const capitalPerPeriod = (loan.loanPrincipal || 0) / numPeriods;
        const currentWeekAmount = payment.amount || loan.paymentAmount || 0;
        const additionalWeeks = untilWeek - payment.week;
        const totalPaidAmount =
          currentWeekAmount + additionalWeeks * capitalPerPeriod;
        setTimeout(() => {
          generatePaymentReceipt(
            transformedLoan,
            payment,
            totalPaidAmount,
            "capital",
            untilWeek,
          );
        }, 500);
      }
    } catch (err) {
      console.error("Capital payment error:", err);
      showMsg(err.message || "Error al registrar abono a capital", true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefinanceBalance = async (loanId, paymentId) => {
    if (actionLoading) return;
    const targetLoan = loans.find((l) => l.id === loanId);
    const periodLabel = "cuota";

    setActionLoading(true);
    try {
      // Call API to refinance
      const refinanceResult = await paymentsApi.refinance(loanId, paymentId, {});

      // Use the loan returned in the POST response to avoid a second round-trip
      // and DynamoDB eventual consistency issues (stale reads after a fresh write)
      const updatedLoan = refinanceResult.loan;
      const transformedLoan = {
        ...updatedLoan,
        id: updatedLoan.loanId,
        quota: updatedLoan.paymentAmount,
        payments: (updatedLoan.payments || []).map((p) => ({
          ...p,
          id: p.paymentId,
          amount: p.amount || updatedLoan.paymentAmount,
        })),
      };

      setLoans((prev) =>
        prev.map((l) => (l.id === loanId ? transformedLoan : l)),
      );
    } catch (err) {
      console.error("Refinance error:", err);
      showMsg(err.message || "Error al refinanciar", true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevertPayment = async (loanId, paymentId) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const result = await paymentsApi.revert(loanId, paymentId);
      const updatedLoan = result.loan;
      const transformedLoan = {
        ...updatedLoan,
        id: updatedLoan.loanId,
        quota: updatedLoan.paymentAmount,
        payments: (updatedLoan.payments || []).map((p) => ({
          ...p,
          id: p.paymentId,
          amount: p.amount || updatedLoan.paymentAmount,
        })),
      };
      setLoans((prev) =>
        prev.map((l) => (l.id === loanId ? transformedLoan : l)),
      );
    } catch (err) {
      console.error("Revert payment error:", err);
      showMsg(err.message || "Error al revertir pago", true);
    } finally {
      setActionLoading(false);
    }
  };

  const selectedLoan = loans.find((l) => l.id === selectedLoanId);

  const activeLoans = loans.filter((loan) => loan.status !== "deleted");
  const deletedLoans = loans.filter((loan) => loan.status === "deleted");

  const filteredLoans = activeLoans.filter((loan) => {
    const matchesSearch = searchTerm.trim()
      ? loan.name.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    let matchesFilter = true;

    if (filterType === "rating") {
      matchesFilter =
        ratingFilter > 0 ? (loan.rating || 0) === ratingFilter : true;
    } else if (filterType === "status") {
      if (statusFilter !== "all") {
        const isPaid = isLoanPaid(loan);
        if (statusFilter === "paid") {
          matchesFilter = isPaid;
        } else if (statusFilter === "pending") {
          matchesFilter = !isPaid;
        } else if (statusFilter === "overdue") {
          // Verificar si tiene cuotas vencidas
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const hasOverdue = (loan.payments || []).some((payment) => {
            const isCapitalOnly = payment.paymentHistory?.some((h) =>
              h.note?.includes("Pago directo a capital, no aplica intereses."),
            );
            if (isCapitalOnly) return false;
            const wasRefinanced = loan.payments.some(
              (pay) => pay.isRefinanced && pay.originalWeek === payment.week,
            );
            if (wasRefinanced) {
              if ((payment.totalPaid || 0) > 0) return false;
              const hasCompensatingPayment = loan.payments.some((pay) => {
                if (Number(pay.week) <= Number(payment.week)) return false;
                if (pay.isRefinanced) return false;
                if (loan.payments.some((p) => p.isRefinanced && p.originalWeek === pay.week)) return false;
                return (pay.totalPaid || 0) >= (pay.amount || 0);
              });
              if (hasCompensatingPayment) return false;
            }
            if (!payment.dueDate) return false;
            const [year, month, day] = payment.dueDate.split("-").map(Number);
            const dueDate = new Date(year, month - 1, day);
            dueDate.setHours(0, 0, 0, 0);
            const remaining = (payment.amount || 0) - (payment.totalPaid || 0);
            return dueDate < today && remaining > 0;
          });
          matchesFilter = hasOverdue;
        }
      }
      // Si statusFilter === 'all', matchesFilter permanece true (muestra todos)
    } else if (filterType === "date") {
      if (dateFrom) {
        // Las fechas se guardan en formato ISO: YYYY-MM-DD
        const startDate = loan.startDate || loan.payments?.[0]?.dueDate;
        if (startDate) {
          // Parsear fecha como fecha local (YYYY-MM-DD)
          const [year, month, day] = startDate.split("-").map(Number);
          const loanStartDate = new Date(year, month - 1, day);
          const filterDate = new Date(dateFrom);

          matchesFilter = loanStartDate >= filterDate;
        } else {
          matchesFilter = false; // Sin fecha de inicio, no coincide
        }
      }
    }

    // Por defecto, ocultar préstamos pagados SOLO cuando NO hay filtros activos
    // Un filtro está activo si:
    // - Hay búsqueda por texto
    // - Hay calificación seleccionada (> 0)
    // - El usuario está usando el filtro de Estado (filterType === 'status')
    // - El usuario está usando el filtro de Fecha con fecha seleccionada
    const hasActiveFilters =
      searchTerm.trim() ||
      ratingFilter > 0 ||
      filterType === "status" ||
      (filterType === "date" && dateFrom);

    if (!hasActiveFilters && isLoanPaid(loan)) {
      return false; // Ocultar préstamos pagados cuando no hay filtros
    }

    return matchesSearch && matchesFilter;
  });

  const sortedLoans = [...filteredLoans].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );
  const displayedLoans = showAll ? sortedLoans : sortedLoans.slice(0, 5);
  const hasMore = filteredLoans.length > 5;

  const menuItems = [
    { id: "dashboard", label: "Inicio",        icon: Home,        iconLight: "text-blue-600",   iconDark: "text-blue-400"   },
    { id: "loans",     label: "Pr\u00E9stamos", icon: DollarSign,  iconLight: "text-amber-600",  iconDark: "text-amber-400"  },
    { id: "caja",      label: "Caja",           icon: Banknote,    iconLight: "text-green-600",  iconDark: "text-green-400"  },
    { id: "tips",      label: "Tips",           icon: Lightbulb,   iconLight: "text-orange-600", iconDark: "text-orange-400" },
    { id: "notes",     label: "Notas",          icon: StickyNote,  iconLight: "text-yellow-600", iconDark: "text-yellow-400" },
    { id: "safacon",   label: "Safac\u00F3n",   icon: Trash,       iconLight: "text-red-600",    iconDark: "text-red-400"    },
  ];

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const isActive =
      activeView === item.id ||
      (activeView === "loanDetail" && item.id === "loans");
    const iconColor = darkMode ? item.iconDark : item.iconLight;
    return (
      <button
        onClick={() => {
          setActiveView(item.id);
          setIsMenuOpen(false);
          if (item.id === "loans") setSelectedLoanId(null);
          if (item.id === "notes") { setNotesSubView("list"); setEditingNote(null); }
        }}
        className={`flex items-center w-full p-3 rounded-xl transition-all duration-200 ${
          isActive
            ? darkMode
              ? "text-white"
              : "text-gray-900"
            : darkMode
            ? "text-gray-400 hover:text-gray-100"
            : "text-gray-400 hover:text-gray-700"
        }`}
      >
        <Icon className={`w-6 h-6 mr-3 transition-opacity duration-200 ${iconColor} ${isActive ? "opacity-100" : "opacity-35"}`} />
        <span className="font-semibold text-base">{item.label}</span>
      </button>
    );
  };

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          darkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <LoginScreen
        onLogin={handleLogin}
        onSignup={handleSignup}
        darkMode={darkMode}
        sessionMsg={sessionKickedMsg}
        onClearSessionMsg={() => setSessionKickedMsg(null)}
      />
    );
  }

  if (pinLocked) {
    return (
      <PinLockModal
        darkMode={darkMode}
        onUnlock={() => { setPinLocked(false); sessionStorage.removeItem("pin-locked"); localStorage.removeItem("app-bg-locked"); }}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div
      className={`h-screen overflow-hidden flex font-sans transition-colors duration-300 ${
        darkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 transform ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        } md:relative md:translate-x-0 transition-all duration-300 w-64 shadow-sm p-4 flex flex-col z-30 border-r ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`}
      >
        <div className="mt-2 px-2">
          <div className="flex flex-col items-center">
            <img
              src={darkMode ? dulusBannerWhite : dulusBannerDark}
              alt="Dulus Loans"
              className="w-full h-auto"
            />
          </div>
          <hr className={`mt-4 border-t ${darkMode ? "border-gray-700" : "border-gray-200"}`} />
        </div>
        <nav className="flex-grow space-y-1">
          {menuItems.map((item) => (
            <NavItem key={item.id} item={item} />
          ))}
        </nav>
      </aside>

      <main className="flex-1 overflow-y-auto pt-0 md:pt-4 pb-0 flex flex-col">
        <header
          className={`md:hidden shadow-sm p-4 flex justify-between items-center sticky top-0 z-10 border-b ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-100"
          }`}
        >
          <div className="flex items-center">
            <button
              onClick={() => setIsMenuOpen(true)}
              className={`p-2 mr-3 rounded-xl transition-all duration-200 ${
                darkMode
                  ? "text-gray-300 hover:bg-gray-700"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1
              className={`text-xl font-black ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Dulus Loans
            </h1>
          </div>
          {/* Avatar + dropdown móvil */}
          <div className="relative z-50" data-user-menu>
            <button
              onClick={() => setShowUserMenu((v) => !v)}
              className={`w-10 h-10 rounded-full font-bold text-sm flex items-center justify-center shadow ${darkMode ? "bg-gray-600 text-gray-100" : "bg-gray-200 text-gray-700"}`}
            >
              {getInitials(user?.name)}
            </button>
            {showUserMenu && (
              <div
                className={`absolute right-0 top-12 w-52 rounded-2xl shadow-xl border z-50 overflow-hidden ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-100"
                }`}
              >
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    setActiveView("editProfile");
                  }}
                  className={`flex items-center justify-between w-full px-4 py-3 border-b transition-colors ${
                    darkMode
                      ? "border-gray-700 hover:bg-gray-700"
                      : "border-gray-100 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`font-bold text-sm ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {user?.name}
                  </span>
                  <Pencil className={`w-3.5 h-3.5 ${darkMode ? "text-gray-400" : "text-gray-400"}`} />
                </button>
                <button
                  onClick={() => {
                    toggleDarkMode();
                    setShowUserMenu(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-sm transition-colors ${
                    darkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {darkMode ? (
                    <Sun className="w-4 h-4 mr-3" />
                  ) : (
                    <Moon className="w-4 h-4 mr-3" />
                  )}
                  {darkMode ? "Modo claro" : "Modo oscuro"}
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className={`flex items-center w-full px-4 py-3 text-sm border-t transition-colors ${
                    darkMode
                      ? "border-gray-700 text-red-400 hover:bg-gray-700"
                      : "border-gray-100 text-red-500 hover:bg-red-50"
                  }`}
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Header escritorio */}
        <header
          className={`hidden md:flex justify-end items-center px-6 py-3 sticky top-0 z-10 border-b ${
            darkMode
              ? "bg-gray-900 border-gray-700"
              : "bg-gray-50 border-gray-100"
          }`}
        >
          <div className="relative z-50" data-user-menu>
            <button
              onClick={() => setShowUserMenu((v) => !v)}
              className={`w-10 h-10 rounded-full font-bold text-sm flex items-center justify-center shadow ${darkMode ? "bg-gray-600 text-gray-100" : "bg-gray-200 text-gray-700"}`}
            >
              {getInitials(user?.name)}
            </button>
            {showUserMenu && (
              <div
                className={`absolute right-0 top-12 w-52 rounded-2xl shadow-xl border z-50 overflow-hidden ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-100"
                }`}
              >
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    setActiveView("editProfile");
                  }}
                  className={`flex items-center justify-between w-full px-4 py-3 border-b transition-colors ${
                    darkMode
                      ? "border-gray-700 hover:bg-gray-700"
                      : "border-gray-100 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`font-bold text-sm ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {user?.name}
                  </span>
                  <Pencil className={`w-3.5 h-3.5 ${darkMode ? "text-gray-400" : "text-gray-400"}`} />
                </button>
                <button
                  onClick={() => {
                    toggleDarkMode();
                    setShowUserMenu(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-sm transition-colors ${
                    darkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {darkMode ? (
                    <Sun className="w-4 h-4 mr-3" />
                  ) : (
                    <Moon className="w-4 h-4 mr-3" />
                  )}
                  {darkMode ? "Modo claro" : "Modo oscuro"}
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className={`flex items-center w-full px-4 py-3 text-sm border-t transition-colors ${
                    darkMode
                      ? "border-gray-700 text-red-400 hover:bg-gray-700"
                      : "border-gray-100 text-red-500 hover:bg-red-50"
                  }`}
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </header>

        {message && (
          <div
            className={`p-4 mx-4 md:mx-auto md:max-w-screen-2xl rounded-lg shadow-lg mb-4 mt-4 ${
              message.isError
                ? "bg-red-50 border border-red-400 text-red-700"
                : "bg-green-50 border border-green-400 text-green-700"
            }`}
          >
            <div className="flex items-center">
              {message.isError ? (
                <X className="w-5 h-5 mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              <p className="text-sm font-medium">{message.text}</p>
            </div>
          </div>
        )}

        <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-4 flex-grow">
          {activeView === "editProfile" && (
            <EditProfileView
              user={user}
              darkMode={darkMode}
              setActionLoading={setActionLoading}
              hiddenBoards={hiddenBoards}
              onUpdateHiddenBoards={updateHiddenBoards}
              onSave={(updated) => {
                const newUser = { ...user, name: updated.name, companyName: updated.companyName ?? user.companyName ?? "", hasPin: updated.hasPin ?? user.hasPin ?? false };
                setUser(newUser);
                localStorage.setItem("user-data", JSON.stringify(newUser));
              }}
            />
          )}
          {activeView === "dashboard" && (
            <Dashboard
                loans={activeLoans}
                darkMode={darkMode}
                cajaAmount={cajaAmount}
                hiddenBoards={hiddenBoards}
                onNavigateToFilter={(view, filterType, filterValue) => {
                  setFilterType(filterType);
                  setStatusFilter(filterValue);
                  setActiveView(view);
                }}
              />
          )}
          {activeView === "caja" && (
            <CajaView
              cajaAmount={cajaAmount}
              onUpdateCaja={updateCaja}
              darkMode={darkMode}
            />
          )}
          {activeView === "tips" && (
            <TipsView
              darkMode={darkMode}
              favoriteTips={favoriteTips}
              onToggleFavorite={handleToggleFavorite}
            />
          )}
          {activeView === "notes" && notesSubView === "list" && (
            <NotesListView
              notes={notes}
              darkMode={darkMode}
              onSelectNote={(note) => {
                setEditingNote(note);
                setNotesSubView("editor");
              }}
              onNewNote={() => {
                setEditingNote(null);
                setNotesSubView("editor");
              }}
              onDeleteNote={(note) => {
                setDeletingNote(note);
                setDeleteNoteModalOpen(true);
              }}
            />
          )}
          {activeView === "notes" && notesSubView === "editor" && (
            <NoteEditorView
              note={editingNote}
              darkMode={darkMode}
              onBack={() => setNotesSubView("list")}
              onNoteCreated={(note) => setNotes((prev) => [note, ...prev])}
              onNoteUpdated={(updatedNote) =>
                setNotes((prev) =>
                  prev
                    .map((n) => (n.noteId === updatedNote.noteId ? updatedNote : n))
                    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                )
              }
              onNoteDeleted={(noteId) => {
                setNotes((prev) => prev.filter((n) => n.noteId !== noteId));
                setNotesSubView("list");
              }}
            />
          )}
          {activeView === "safacon" && (
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2
                  className={`text-2xl font-black ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {"Safac\u00F3n"}
                </h2>
                {deletedLoans.length > 0 && (
                  <div className="relative w-full sm:w-auto">
                    <input
                      type="text"
                      placeholder="Buscar por nombre..."
                      value={safaconSearch}
                      onChange={(e) => setSafaconSearch(e.target.value)}
                      className={`w-full sm:w-64 pl-10 pr-4 py-2.5 border-2 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 font-medium ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                          : "bg-white border-gray-200 text-gray-900 hover:border-gray-300"
                      }`}
                    />
                    <svg
                      className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              {deletedLoans.length === 0 ? (
                <div
                  className={`p-12 rounded-2xl shadow-md text-center border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-100"
                  }`}
                >
                  <Trash2
                    className={`w-12 h-12 mx-auto mb-3 ${
                      darkMode ? "text-gray-600" : "text-gray-300"
                    }`}
                  />
                  <p
                    className={`text-lg font-semibold ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {"Safac\u00F3n vac\u00EDo"}
                  </p>
                  <p
                    className={`text-sm mt-1 ${
                      darkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {"Los pr\u00E9stamos eliminados aparecer\u00E1n aqu\u00ED"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {deletedLoans
                    .filter((loan) =>
                      safaconSearch.trim()
                        ? loan.name.toLowerCase().includes(safaconSearch.toLowerCase())
                        : true,
                    )
                    .map((loan) => (
                    <div key={loan.id}>
                      <LoanCard
                        loan={loan}
                        onSelect={(id) => {
                          setSelectedLoanId(id);
                          setActiveView("loanDetail");
                        }}
                        onEdit={() => {}}
                        onDelete={() => handlePermanentDelete(loan)}
                        onRatingChange={() => {}}
                        onPayAll={() => {}}
                        onWhatsAppReminder={() => {}}
                        darkMode={darkMode}
                        deleted={true}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeView === "loanDetail" && selectedLoan && (
            <LoanDetailsView
              loan={selectedLoan}
              onRegisterPayment={handleRegisterPayment}
              onRefinanceBalance={handleRefinanceBalance}
              onCapitalPayment={handleCapitalPayment}
              onRevertPayment={handleRevertPayment}
              darkMode={darkMode}
              actionLoading={actionLoading}
              readOnly={selectedLoan.status === "deleted"}
              onBack={() => {
                setSelectedLoanId(null);
                setActiveView("loans");
              }}
            />
          )}
          {activeView === "loans" && (
            <div className="p-4 sm:p-6">
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h2
                    className={`text-2xl font-black ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Préstamos
                  </h2>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-initial">
                      <input
                        type="text"
                        placeholder="Buscar por nombre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full sm:w-64 pl-10 pr-4 py-2.5 border-2 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 font-medium ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                            : "bg-white border-gray-200 text-gray-900 hover:border-gray-300"
                        }`}
                      />
                      <svg
                        className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <button
                      onClick={() => {
                        setEditingLoan(null);
                        setModalOpen(true);
                      }}
                      className="flex items-center justify-center w-12 h-12 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-900 hover:shadow-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex-shrink-0"
                    >
                      <Plus className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {activeLoans.length > 0 && (
                  <div className="flex flex-row gap-3 items-center flex-wrap">
                    <select
                      value={filterType}
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setRatingFilter(0);
                        setStatusFilter("all");
                        setDateFrom("");
                      }}
                      className={`flex-1 min-w-[140px] max-w-[200px] px-4 py-2.5 border-2 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500 font-semibold transition-all duration-200 ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                          : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <option value="rating">Calificación</option>
                      <option value="status">Estado</option>
                      <option value="date">Fecha inicio</option>
                    </select>

                    {filterType === "rating" && (
                      <select
                        value={ratingFilter}
                        onChange={(e) =>
                          setRatingFilter(Number(e.target.value))
                        }
                        className={`flex-1 min-w-[140px] max-w-[200px] px-4 py-2.5 border-2 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500 font-semibold transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                            : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <option value={0}>Todas</option>
                        <option value={1}>⭐</option>
                        <option value={2}>⭐⭐</option>
                        <option value={3}>⭐⭐⭐</option>
                        <option value={4}>⭐⭐⭐⭐</option>
                        <option value={5}>⭐⭐⭐⭐⭐</option>
                      </select>
                    )}

                    {filterType === "status" && (
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`flex-1 min-w-[140px] max-w-[200px] px-4 py-2.5 border-2 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500 font-semibold transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                            : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <option value="all">Todos</option>
                        <option value="paid">Pagados</option>
                        <option value="pending">Pendientes</option>
                        <option value="overdue">Atrasados</option>
                      </select>
                    )}

                    {filterType === "date" && (
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className={`flex-1 min-w-[140px] max-w-[200px] px-4 py-2.5 border-2 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500 font-semibold transition-all duration-200 ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white hover:border-gray-500"
                            : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                        }`}
                        placeholder="Desde"
                      />
                    )}
                  </div>
                )}
              </div>

              {filteredLoans.length === 0 &&
              (searchTerm ||
                ratingFilter > 0 ||
                statusFilter !== "all" ||
                dateFrom) ? (
                <div
                  className={`p-12 rounded-2xl shadow-md text-center border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-100"
                  }`}
                >
                  <ListChecks
                    className={`w-12 h-12 mx-auto mb-3 ${
                      darkMode ? "text-gray-600" : "text-gray-300"
                    }`}
                  />
                  <p
                    className={`text-lg font-semibold ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Sin resultados
                  </p>
                  <p
                    className={`text-sm mt-1 ${
                      darkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    No se encontraron préstamos con los filtros aplicados
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {activeLoans.length === 0 ? (
                      <div
                        className={`col-span-full p-12 rounded-2xl shadow-md text-center border ${
                          darkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-white border-gray-100"
                        }`}
                      >
                        <DollarSign
                          className={`w-12 h-12 mx-auto mb-3 ${
                            darkMode ? "text-gray-600" : "text-gray-300"
                          }`}
                        />
                        <p
                          className={`text-lg font-semibold ${
                            darkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          No hay préstamos
                        </p>
                        <p
                          className={`text-sm mt-1 ${
                            darkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          Haz clic en + para agregar el primero
                        </p>
                      </div>
                    ) : (
                      displayedLoans.map((loan) => (
                        <LoanCard
                          key={loan.id}
                          loan={loan}
                          onSelect={(id) => {
                            setSelectedLoanId(id);
                            setActiveView("loanDetail");
                          }}
                          onEdit={(loan) => {
                            setEditingLoan(loan);
                            setModalOpen(true);
                          }}
                          onDelete={(loan) => {
                            setDeletingLoan(loan);
                            setDeleteModalOpen(true);
                          }}
                          onRatingChange={handleRatingChange}
                          onPayAll={payAllPendingWeeks}
                          onWhatsAppReminder={generatePaymentReminder}
                          darkMode={darkMode}
                        />
                      ))
                    )}
                  </div>

                  {hasMore && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={() => setShowAll(!showAll)}
                          className="px-6 py-3 bg-gray-800 text-white rounded-xl shadow-md hover:bg-gray-900 hover:shadow-lg hover:scale-105 transition-all duration-300 font-bold"
                        >
                          {showAll
                            ? `Mostrar menos (${displayedLoans.length} de ${filteredLoans.length})`
                            : `Ver todos (${filteredLoans.length})`}
                        </button>
                      </div>
                    )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer
          className={`py-4 text-center border-t ${
            darkMode
              ? "border-gray-700 text-gray-500"
              : "border-gray-200 text-gray-400"
          }`}
        >
          <p className="text-xs font-medium">
            © 2025 Dulus Solutions. Todos los derechos reservados.
          </p>
        </footer>
      </main>

      <LoanModal
        loan={editingLoan}
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingLoan(null);
        }}
        onSave={handleSave}
        darkMode={darkMode}
        actionLoading={actionLoading}
      />
      <DeleteModal
        loan={deletingLoan}
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingLoan(null);
        }}
        onConfirm={handleDelete}
        darkMode={darkMode}
        actionLoading={actionLoading}
      />
      <DeleteNoteModal
        note={deletingNote}
        isOpen={deleteNoteModalOpen}
        onClose={() => {
          setDeleteNoteModalOpen(false);
          setDeletingNote(null);
        }}
        onConfirm={handleDeleteNote}
        darkMode={darkMode}
        actionLoading={noteActionLoading}
      />

      {actionLoading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] backdrop-blur-sm">
          <div
            className={`p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-3 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <Loader2
              className={`w-10 h-10 animate-spin ${
                darkMode ? "text-green-400" : "text-gray-700"
              }`}
            />
            <span
              className={`text-sm font-semibold ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Procesando...
            </span>
          </div>
        </div>
      )}

    </div>
  );
};

export default App;
